import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'raviger';
import './ComplianceListingV2.css';
import ComplianceActivityDrawer from './ComplianceActivityDrawer';
import Select from 'react-select';
import { sortBy, uniq } from 'underscore';
import { useGetCompanies, useGetCompanyLocations, useGetDepartmentUserMappings, useGetActs, useGetRules, useGetActivities } from '../../../../backend/masters';
import { useGetUserCompanies as useGetUserCompaniesTree } from '../../../../backend/query';
import { FULL_USER_COMPANIES_PAYLOAD } from '../../../../backend/common/noticeManagementUtils';
import { useGetAllComplianceActivities } from '../../../../backend/compliance';
import { getUserDetails, hasUserAccess } from '../../../../backend/auth';
import { setUserDetailsInFilters } from '../../../../constants/Compliance.constants';
import { USER_PRIVILEGES } from '../../UserManagement/Roles/RoleConfiguration';
import { DEFAULT_OPTIONS_PAYLOAD } from '../../../common/Table';
import { post } from '../../../../backend/request';
import { useGetComplianceDashboardScoreCards, ComplianceDashboardPayload } from '../../../../backend/complianceDashboardV2';
import { downloadFileContent, downloadWithPresignedUrl, readBlobAsJson } from '../../../../utils/common';
import ComplianceBulkUploadModal from '../TaskManagement/ComplianceBulkUploadModal';
import ComplianceBulkReviewModal from '../TaskManagement/ComplianceBulkReviewModal';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import iconEdit     from './assets/cl2-icon-edit.svg';
import iconDownload from './assets/cl2-icon-download.svg';

// ── Constants ──────────────────────────────────────────────────────────────────

const DEFAULT_VALUE  = 'ALL';
const DEFAULT_OPTION = { value: DEFAULT_VALUE, label: 'All' };
const PAGE_SIZE      = 25;   // default; user can change via "Results per page" selector
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const ACTIVITY_TABS = [
    'All', 'Appointment', 'Assurance', 'Audit', 'Deposit',
    'Display', 'Internal Compliance', 'Intimation', 'Meeting', 'Policy',
    'Registers', 'Registration', 'Returns', 'Rule',
];

// Month options for the cross-browser Month & Year picker
const MONTH_OPTIONS = [
    { value: '01', label: 'January'   }, { value: '02', label: 'February'  },
    { value: '03', label: 'March'     }, { value: '04', label: 'April'     },
    { value: '05', label: 'May'       }, { value: '06', label: 'June'      },
    { value: '07', label: 'July'      }, { value: '08', label: 'August'    },
    { value: '09', label: 'September' }, { value: '10', label: 'October'   },
    { value: '11', label: 'November'  }, { value: '12', label: 'December'  },
];
function buildMonthYearOptions() {
    const cy = new Date().getFullYear();
    const years: number[] = [];
    for (let y = cy; y >= cy - 4; y--) years.push(y);
    return years;
}

// The 4 statuses the calendar panel displays — auto-highlighted when arriving
// from a calendar redirect (dashboardNav.kind === 'calendar').
const CALENDAR_PILL_KEYS = new Set(['Due', 'NonCompliant', 'Submitted', 'Rejected']);

const STATUS_PILLS = [
    { key: 'Approved',     label: 'Compliant',        cls: 'compliant'     },
    { key: 'NonCompliant', label: 'Non-Compliant',     cls: 'non-compliant' },
    { key: 'Due',          label: 'Upcoming',          cls: 'upcoming'      },
    { key: 'Rejected',     label: 'Rejected',          cls: 'rejected'      },
    { key: 'Submitted',    label: 'Approval Pending',  cls: 'pending'       },
    { key: 'Recalled',     label: 'Recalled',          cls: 'recalled'      },
];

// Maps status pill key → field name in the scoreCards API response
const PILL_COUNT_KEYS: Record<string, string> = {
    Approved:     'approved',
    NonCompliant: 'non_compliant',
    Due:          'due_overdue',
    Rejected:     'rejected',
    Submitted:    'pending_review',
    Recalled:     'recalled',
};

const PRIORITY_OPTIONS = ['All', 'Critical', 'Medium', 'Low'];

// Status badge style map
const BADGE_CLASS: Record<string, string> = {
    Approved:     'cl2-badge cl2-badge-approved',
    Due:          'cl2-badge cl2-badge-due',
    NonCompliant: 'cl2-badge cl2-badge-non-compliant',
    Submitted:    'cl2-badge cl2-badge-pending',
    Rejected:     'cl2-badge cl2-badge-rejected',
    Recalled:     'cl2-badge cl2-badge-recalled',
};

// Display label for status badge
const STATUS_LABEL: Record<string, string> = {
    Approved:     'Compliant',
    Due:          'Upcoming',
    NonCompliant: 'Non-Compliant',
    Submitted:    'Approval Pending',
    Rejected:     'Rejected',
    Recalled:     'Recalled',
};

// Association includes needed by the list API
const INCLUDES_ASSOCIATIONS = [
    'associate_company', 'company', 'location', 'act', 'rule',
    'activity', 'compliance_manager', 'department', 'vertical',
    'act_state_mapping', 'compliance_owner',
];
const INCLUDE_ASSOCIATION_COLUMNS = {
    associate_company:  { only: ['id', 'name', 'code'] },
    company:            { only: ['id', 'name', 'code'] },
    location:           { only: ['id', 'name', 'code'] },
    act:                { include: { law: { only: ['id', 'name', 'description'] } } },
    rule:               { only: ['id', 'name', 'rule_no', 'section_no', 'description'] },
    activity:           { only: ['id', 'name', 'activity_type', 'periodicity'] },
    compliance_manager: { only: ['id', 'name'] },
    department:         { only: ['id', 'name'] },
    vertical:           { only: ['id', 'name'] },
    act_state_mapping:  { include: ['rule_compliance_detail'] },
    compliance_owner:   { only: ['id', 'name'] },
};

// ── Time Range ─────────────────────────────────────────────────────────────────

interface TimeRangeOption {
    value: string;
    label: string;
    startDate: string;  // YYYY-MM-DD
    endDate:   string;  // YYYY-MM-DD
}

function getCurrentFYStartYear(): number {
    const now   = new Date();
    const month = now.getMonth() + 1;
    return month >= 4 ? now.getFullYear() : now.getFullYear() - 1;
}

function fyRange(fyStart: number): { startDate: string; endDate: string } {
    return {
        startDate: `${fyStart}-04-01`,
        endDate:   `${fyStart + 1}-03-31`,
    };
}

function generateTimeRangeOptions(): TimeRangeOption[] {
    const fyStart   = getCurrentFYStartYear();
    const currentFY = `${fyStart}-${String(fyStart + 1).slice(-2)}`;

    const opts: TimeRangeOption[] = [
        { value: 'full', label: `Current Year (${fyStart})`, ...fyRange(fyStart) },
        { value: 'q1',   label: `Q1 ${fyStart}`,       startDate: `${fyStart}-04-01`,     endDate: `${fyStart}-06-30`     },
        { value: 'q2',   label: `Q2 ${fyStart}`,       startDate: `${fyStart}-07-01`,     endDate: `${fyStart}-09-30`     },
        { value: 'q3',   label: `Q3 ${fyStart}`,       startDate: `${fyStart}-10-01`,     endDate: `${fyStart}-12-31`     },
        { value: 'q4',   label: `Q4 ${fyStart + 1}`,   startDate: `${fyStart + 1}-01-01`, endDate: `${fyStart + 1}-03-31` },
    ];

    // Current FY as an explicit selectable entry, then past FYs
    opts.push({ value: `fy_${currentFY}`, label: `FY ${currentFY}`, ...fyRange(fyStart) });
    for (let i = 1; i <= 5; i++) {
        const s = fyStart - i;
        opts.push({
            value: `fy_${s}-${String(s + 1).slice(-2)}`,
            label: `FY ${s}-${String(s + 1).slice(-2)}`,
            ...fyRange(s),
        });
    }
    return opts;
}

const TIME_RANGE_OPTIONS  = generateTimeRangeOptions();
const DEFAULT_TIME_RANGE  = TIME_RANGE_OPTIONS[0];

// ── react-select styles ────────────────────────────────────────────────────────

const selectStyles = {
    control: (base: any) => ({
        ...base,
        minHeight: 38,
        height: 38,
        fontSize: 14,
        borderColor: '#e6e6e6',
        borderRadius: 8,
        boxShadow: 'none',
        '&:hover': { borderColor: '#2965ad' },
    }),
    valueContainer:      (base: any) => ({ ...base, padding: '0 12px' }),
    indicatorSeparator:  ()          => ({ display: 'none' }),
    dropdownIndicator:   (base: any) => ({ ...base, padding: '0 8px' }),
    menu:                (base: any) => ({ ...base, fontSize: 13, zIndex: 9999 }),
    menuPortal:          (base: any) => ({ ...base, zIndex: 9999 }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected ? '#2965ad' : state.isFocused ? '#f0f5ff' : '#fff',
        color: state.isSelected ? '#fff' : '#1e1e1e',
    }),
    singleValue: (base: any) => ({ ...base, color: '#1e1e1e', fontFamily: 'Montserrat, sans-serif' }),
    placeholder: (base: any) => ({ ...base, color: '#757575' }),
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function Skeleton({ w }: { w?: number | string }) {
    return (
        <span
            className="cl2-skeleton"
            style={{ width: w !== undefined ? w : '80%' }}
        />
    );
}

function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
    return (
        <svg
            className={`cl2-sort-icon${active ? ' active' : ''}`}
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            {dir === 'asc' || !active ? (
                <path d="M6 9L2 4h8L6 9z" fill={active ? '#1e1e1e' : '#9ca3af'} />
            ) : (
                <path d="M6 3l4 5H2L6 3z" fill="#1e1e1e" />
            )}
        </svg>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────────

// ── Penalty filter type from dashboard ────────────────────────────────────────
type PenaltyType = 'financial_exposure' | 'imprisonment_risk' | 'license_revocation';

const PENALTY_LABELS: Record<PenaltyType, string> = {
    financial_exposure:  'Financial Exposure',
    imprisonment_risk:   'Imprisonment Risk',
    license_revocation:  'License Revocation',
};

// Convert a dashboard financial_year + quarter to start/end date strings
function fyToDateRange(financialYear: string, quarter: string): { startDate: string; endDate: string } {
    const startYear = parseInt(financialYear.split('-')[0], 10);
    if (quarter === 'q1') return { startDate: `${startYear}-04-01`,     endDate: `${startYear}-06-30` };
    if (quarter === 'q2') return { startDate: `${startYear}-07-01`,     endDate: `${startYear}-09-30` };
    if (quarter === 'q3') return { startDate: `${startYear}-10-01`,     endDate: `${startYear}-12-31` };
    if (quarter === 'q4') return { startDate: `${startYear + 1}-01-01`, endDate: `${startYear + 1}-03-31` };
    // Full year
    return { startDate: `${startYear}-04-01`, endDate: `${startYear + 1}-03-31` };
}

export default function ComplianceListingV2() {
    // ── Dashboard navigation state (set once on mount, covers penalty + KPI) ──
    const { state: navState }: any = useHistory();

    interface DashboardNav {
        kind: 'penalty' | 'kpi' | 'chart' | 'owner' | 'manager' | 'calendar' | 'pair';
        label: string;               // banner text (penalty only; unused for others)
        dashboardPayload: any;
        // penalty only:
        penaltyType?: PenaltyType;
        // kpi only:
        statusFilter?: string | null; // null = no status filter (Total Activities)
        // chart only:
        chartType?: 'category' | 'location';
        chartActivityType?: string;   // for category chart
        chartLocationId?: number;     // for location chart
        chartLocationName?: string;
        chartStatus?: string;         // 'NonCompliant' | 'Due' | 'Approved'
        // owner accountability only (manager login):
        ownerId?: number;
        ownerName?: string;
        ownerStatusFilter?: string | null; // null = Total (no status filter)
        // manager accountability only (owner login):
        managerId?: number;
        managerName?: string;
        managerStatusFilter?: string | null;
        // pair accountability only (escalation login):
        pairOwnerId?: any;
        pairOwnerName?: string;
        pairManagerId?: any;
        pairManagerName?: string;
        pairStatusFilter?: string | null;
        // calendar only:
        calendarDate?: string; // 'YYYY-MM-DD'
    }

    // Human-readable status label for chart nav (used if we ever show a banner)
    function chartStatusLabel(s: string): string {
        if (s === 'NonCompliant') return 'Non-Compliant';
        if (s === 'Due')         return 'Upcoming';
        if (s === 'Approved')    return 'Compliant';
        if (s === 'Other')       return 'Other Activities';
        return s;
    }

    // dashboardNav is initialised once from navState and can be cleared to null
    // by calling setDashboardNav(null) when the user applies their own filters,
    // which switches buildListPayload() to the normal local-state path.
    const [dashboardNav, setDashboardNav] = useState<DashboardNav | null>(function() {
        if (!navState || !navState.dashboard_payload) return null;
        if (navState.from_penalty) {
            return {
                kind:            'penalty',
                label:           PENALTY_LABELS[navState.from_penalty as PenaltyType] || navState.from_penalty,
                dashboardPayload: navState.dashboard_payload,
                penaltyType:     navState.from_penalty as PenaltyType,
            };
        }
        if (navState.from_kpi !== undefined) {
            return {
                kind:            'kpi',
                label:           navState.from_kpi,
                dashboardPayload: navState.dashboard_payload,
                statusFilter:    navState.status_filter,
            };
        }
        if (navState.from_chart) {
            var cType = navState.from_chart as 'category' | 'location';
            var cStatus = navState.chart_status || '';
            var ctxLabel = cType === 'category'
                ? (navState.chart_activity_type || '') + ' — ' + chartStatusLabel(cStatus)
                : (navState.chart_location_name  || '') + ' — ' + chartStatusLabel(cStatus);
            return {
                kind:               'chart',
                label:              ctxLabel,
                dashboardPayload:   navState.dashboard_payload,
                chartType:          cType,
                chartActivityType:  navState.chart_activity_type,
                chartLocationId:    navState.chart_location_id,
                chartLocationName:  navState.chart_location_name,
                chartStatus:        cStatus,
            };
        }
        if (navState.from_owner) {
            return {
                kind:              'owner',
                label:             navState.owner_name || 'Owner',
                dashboardPayload:  navState.dashboard_payload,
                ownerId:           navState.owner_id,
                ownerName:         navState.owner_name,
                ownerStatusFilter: navState.status_filter,
            };
        }
        if (navState.from_manager) {
            return {
                kind:                'manager',
                label:               navState.manager_name || 'Manager',
                dashboardPayload:    navState.dashboard_payload,
                managerId:           navState.manager_id,
                managerName:         navState.manager_name,
                managerStatusFilter: navState.status_filter,
            };
        }
        if (navState.from_pair) {
            return {
                kind:             'pair',
                label:            (navState.owner_name || 'Owner') + ' × ' + (navState.manager_name || 'Manager'),
                dashboardPayload: navState.dashboard_payload,
                pairOwnerId:      navState.owner_id,
                pairOwnerName:    navState.owner_name,
                pairManagerId:    navState.manager_id,
                pairManagerName:  navState.manager_name,
                pairStatusFilter: navState.status_filter,
            };
        }
        if (navState.from_calendar) {
            return {
                kind:             'calendar',
                label:            navState.calendar_date || '',
                dashboardPayload: navState.dashboard_payload,
                calendarDate:     navState.calendar_date,
            };
        }
        return null;
    });

    // ── Filter state ───────────────────────────────────────────────────────────
    const [filters, setFilters] = useState<any>({
        company:           DEFAULT_OPTION,
        associate_company: null,
        state:             null,
        location:          null,
    });
    const filtersRef = useRef<any>();
    filtersRef.current = filters;

    const [timeRange,       setTimeRange]       = useState(function() {
        // When arriving from the dashboard, pre-select the matching time range option
        // so the dropdown label reflects what was chosen on the dashboard.
        var dp = navState && navState.dashboard_payload;
        if (dp) {
            // Quarter selection (q1–q4)
            if (dp.quarter && ['q1','q2','q3','q4'].indexOf(dp.quarter) !== -1) {
                return dp.quarter;
            }
            // Past FY  (e.g. "2025-26" → option value "fy_2025-26")
            if (dp.financial_year) {
                var fyVal = 'fy_' + dp.financial_year;
                for (var _i = 0; _i < TIME_RANGE_OPTIONS.length; _i++) {
                    if (TIME_RANGE_OPTIONS[_i].value === fyVal) return fyVal;
                }
                // Not in past-FY list → it's the current FY → 'full'
                return 'full';
            }
        }
        return DEFAULT_TIME_RANGE.value;
    });
    // Pre-populate from dashboard nav so the correct pill(s) show as active on arrival.
    // When the user clicks a pill, setDashboardNav(null) is also called so the normal
    // path takes over and respects selectedStatuses from that point on.
    const [selectedStatuses, setSelectedStatuses] = useState<Record<string, boolean>>(function() {
        // Calendar redirect: pre-select the 4 statuses the calendar displays.
        // Checked first (before the dashboard_payload guard) because handleCalViewMore
        // has no apiPayload guard, so dashboard_payload may be absent in edge cases.
        if (navState && navState.from_calendar) {
            return { Due: true, NonCompliant: true, Submitted: true, Rejected: true };
        }
        if (!navState || !navState.dashboard_payload) return {};
        // Penalty cards are always a subset of Non Compliant — pre-select that pill
        if (navState.from_penalty) return { NonCompliant: true };
        var key: string | null = null;
        if (navState.from_kpi !== undefined) key = navState.status_filter || null;
        else if (navState.from_chart)        key = navState.chart_status  || null;
        else if (navState.from_owner)        key = navState.status_filter || null;
        else if (navState.from_manager)      key = navState.status_filter || null;
        else if (navState.from_pair)         key = navState.status_filter || null;
        if (key) { var m: Record<string, boolean> = {}; m[key] = true; return m; }
        return {};
    });
    const [selectedTab,     setSelectedTab]     = useState(function() {
        // Pre-select the category tab when arriving from a category chart segment click
        if (navState && navState.from_chart === 'category' && navState.chart_activity_type) {
            return navState.chart_activity_type;
        }
        return 'All';
    });
    const [selectedPriority,setSelectedPriority]= useState('All');
    const [priorityDropOpen, setPriorityDropOpen] = useState(false);
    const priorityDropRef  = useRef<HTMLDivElement>(null);
    const tableScrollRef   = useRef<HTMLDivElement>(null);
    const [search,          setSearch]          = useState('');
    const [searchInput,     setSearchInput]     = useState('');

    // ── Pagination & sort ──────────────────────────────────────────────────────
    const [page,     setPage]     = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    // Default sort: nearest due date first so activities requiring attention appear at the top.
    const [sortCol,  setSortCol]  = useState('due_date');
    const [sortDir,  setSortDir]  = useState<'asc' | 'desc'>('asc');

    // ── Dropdown option lists ──────────────────────────────────────────────────
    const companiesLoadedRef = useRef(false);
    const [companies,          setCompanies]          = useState<any[]>([DEFAULT_OPTION]);
    const [associateCompanies, setAssociateCompanies] = useState<any[] | undefined>();
    const [allLocations,       setAllLocations]       = useState<any[]>([]);
    const [locations,          setLocations]          = useState<any[] | undefined>();
    const [states,             setStates]             = useState<any[]>([]);
    const [owners,       setOwners]       = useState<any[]>([]);
    const [managers,     setManagers]     = useState<any[]>([]);
    const [verticals,    setVerticals]    = useState<any[]>([]);
    const [departments,  setDepartments]  = useState<any[]>([]);

    // ── More Filters popover ───────────────────────────────────────────────────
    const [showMore, setShowMore] = useState(false);
    // Committed values — feed into listPayload / API
    const [dueDateFrom,        setDueDateFrom]        = useState(function() {
        var cd = navState && navState.from_calendar && navState.calendar_date;
        return cd ? cd : '';
    });
    const [dueDateTo,          setDueDateTo]          = useState(function() {
        var cd = navState && navState.from_calendar && navState.calendar_date;
        return cd ? cd : '';
    });
    const [monthYear,          setMonthYear]          = useState('');   // "YYYY-MM"
    const [estTypeFilter,         setEstTypeFilter]         = useState('');
    const [actIdFilter,           setActIdFilter]           = useState<number | null>(null);
    const [actNameFilter,         setActNameFilter]         = useState('');   // display label paired with actIdFilter
    const [ruleIdFilter,          setRuleIdFilter]          = useState<number | null>(null);
    const [ruleNameFilter,        setRuleNameFilter]        = useState('');   // display label paired with ruleIdFilter
    const [activityIdFilter,      setActivityIdFilter]      = useState<number | null>(null);
    const [activityNameFilter,    setActivityNameFilter]    = useState('');   // display label paired with activityIdFilter
    const [ownerFilter,           setOwnerFilter]           = useState<number | null>(function() {
        // Only carry over dashboard's owner_id when the current user is a Manager or Escalation
        // (roles where dp.owner_id was explicitly user-selected).  For Owner users dp.owner_id
        // is always the logged-in user's own ID (auto-injected by setUserDetailsInFilters on
        // the dashboard), so pre-populating ownerFilter would show a phantom "+1" badge in
        // More Filters even though no filter was deliberately chosen.
        var canSeeOwnerDropdown = hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) ||
                                  hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);
        if (!canSeeOwnerDropdown) return null;
        // Accountability click: owner_id comes directly in navState (not inside dashboard_payload)
        if (navState && (navState.from_owner || navState.from_pair) && navState.owner_id)
            return navState.owner_id;
        var dp = navState && navState.dashboard_payload;
        if (dp && dp.owner_id && dp.owner_id.length) return dp.owner_id[0];
        return null;
    });
    const [managerFilter,         setManagerFilter]         = useState<number | null>(function() {
        // Only carry over dashboard's manager_id when the current user is an Owner or Escalation
        // (roles where dp.manager_id was explicitly user-selected).  For Manager users
        // dp.manager_id is always the logged-in user's own ID (auto-injected).
        var canSeeManagerDropdown = hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) ||
                                    hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);
        if (!canSeeManagerDropdown) return null;
        // Accountability click: manager_id comes directly in navState (not inside dashboard_payload)
        if (navState && (navState.from_manager || navState.from_pair) && navState.manager_id)
            return navState.manager_id;
        var dp = navState && navState.dashboard_payload;
        if (dp && dp.manager_id && dp.manager_id.length) return dp.manager_id[0];
        return null;
    });
    const [verticalFilter,        setVerticalFilter]        = useState<number | null>(null);
    const [departmentFilter,      setDepartmentFilter]      = useState<number | null>(null);
    // Draft values — bound to the inputs; only committed when Apply is clicked
    const [draftDueDateFrom,      setDraftDueDateFrom]      = useState(function() {
        var cd = navState && navState.from_calendar && navState.calendar_date;
        return cd ? cd : '';
    });
    const [draftDueDateTo,        setDraftDueDateTo]        = useState(function() {
        var cd = navState && navState.from_calendar && navState.calendar_date;
        return cd ? cd : '';
    });
    const [draftMonthYear,        setDraftMonthYear]        = useState('');
    // Separate halves for the two-select Month & Year picker (see filterMonth/filterYear note)
    const [draftMYMonth, setDraftMYMonth] = useState('');
    const [draftMYYear,  setDraftMYYear]  = useState('');
    const [draftEstType,          setDraftEstType]          = useState('');
    const [draftActId,            setDraftActId]            = useState<number | null>(null);
    const [draftActSearch,        setDraftActSearch]        = useState('');
    const [draftRuleId,           setDraftRuleId]           = useState<number | null>(null);
    const [draftRuleSearch,       setDraftRuleSearch]       = useState('');
    const [draftActivityId,       setDraftActivityId]       = useState<number | null>(null);
    const [draftActivitySearch,   setDraftActivitySearch]   = useState('');
    // Dropdown-open flags for the three typeahead fields — separate from the committed
    // id so that clicking the input shows results without destroying the selection.
    const [actDropdownOpen,      setActDropdownOpen]      = useState(false);
    const [ruleDropdownOpen,     setRuleDropdownOpen]     = useState(false);
    const [activityDropdownOpen, setActivityDropdownOpen] = useState(false);
    const [draftOwner,       setDraftOwner]       = useState<any>(function() {
        // Pre-populate from accountability redirect so the More Filters drawer shows the name immediately
        if (navState && (navState.from_owner || navState.from_pair) && navState.owner_id && navState.owner_name)
            return { value: navState.owner_id, label: navState.owner_name };
        return null;
    });
    const [draftManager,     setDraftManager]     = useState<any>(function() {
        if (navState && (navState.from_manager || navState.from_pair) && navState.manager_id && navState.manager_name)
            return { value: navState.manager_id, label: navState.manager_name };
        return null;
    });
    const [draftVertical,    setDraftVertical]    = useState<any>(null);
    const [draftDepartment,  setDraftDepartment]  = useState<any>(null);

    // ── Export ─────────────────────────────────────────────────────────────────
    const [exporting, setExporting] = useState(false);
    const [drawerActivity, setDrawerActivity] = useState<any | null>(null);
    const [drawerMode,    setDrawerMode]     = useState<'view' | 'edit'>('view');
    const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());
    const [bulkUploadOpen,  setBulkUploadOpen]  = useState(false);
    const [bulkReviewOpen,  setBulkReviewOpen]  = useState(false);

    const isOwner      = hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD);
    const isManager    = hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD);
    const isEscalation = hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);
    // Either owner or manager can select rows for bulk actions; escalation is read-only
    const canSelect = isOwner || isManager || isEscalation;

    const { user_id: loginUser } = getUserDetails();

    // ── Dropdown data APIs ─────────────────────────────────────────────────────

    // Escalation: single call returns the full company → AC → location tree.
    const { userCompanies, isFetching: fetchingUserCompanies } =
        useGetUserCompaniesTree(FULL_USER_COMPANIES_PAYLOAD, isEscalation);

    // Dept mappings: escalation scopes to selected company; owner/manager fetches all.
    const escalationCompanyId = isEscalation ? ((filters.company || {}).value || null) : null;
    const { departmentUsers, isFetching: fetchingDeptUsers }: any = useGetDepartmentUserMappings(
        isEscalation
            ? { ...DEFAULT_OPTIONS_PAYLOAD, filters: { department_company_id_eq: escalationCompanyId, is_active_eq: true } }
            : { ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_active_eq: true } },
        isEscalation ? (!!escalationCompanyId && escalationCompanyId !== DEFAULT_VALUE) : true,
    );

    // Non-escalation only: separate cascading API calls for ACs and locations.
    const { companies: acs, isFetching: fetchingAcs } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
            is_parent_eq: 'false',
            parent_company_id_eq: (filters.company || {}).value,
            is_active_eq: true,
        },
        sorts: ['name asc'],
    }, !isEscalation && filters.company && filters.company.value !== DEFAULT_VALUE);

    const { locations: locs, isFetching: fetchingLocs } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: { company_id_eq: (filters.associate_company || {}).value },
        include:  { location: ['state'] },
        includes: { location: ['state'] },
        sort: ['location_name asc'],
    }, !isEscalation && Boolean(filters.associate_company && filters.associate_company.value && filters.associate_company.value !== DEFAULT_VALUE));

    // ── Effects: cascade ──────────────────────────────────────────────────────

    // Dept-mapping effect: branches for escalation vs owner/manager roles.
    useEffect(() => {
        if (fetchingDeptUsers || !departmentUsers) return;

        if (isEscalation) {
            // Company-scoped response: classify users as owners/managers; collect verticals/depts.
            // Companies come from the user_companies hook (separate effect below).
            const _owners:      any[] = [];
            const _managers:    any[] = [];
            const _verticals:   any   = {};
            const _departments: any   = {};
            (departmentUsers as any[]).forEach(({ department, user }: any) => {
                const { vertical } = department;
                if (!_verticals[vertical.id])
                    _verticals[vertical.id] = { value: vertical.id, label: vertical.name, company_id: (vertical.company || {}).id };
                if (!_departments[department.id])
                    _departments[department.id] = { value: department.id, label: department.name, company_id: (vertical.company || {}).id, verticalId: vertical.id };
                const isOwnerUser   = (user.roles || []).some(({ pages }: any) => (pages || []).includes(USER_PRIVILEGES.OWNER_DASHBOARD));
                const isManagerUser = (user.roles || []).some(({ pages }: any) => (pages || []).includes(USER_PRIVILEGES.MANAGER_DASHBOARD));
                if (isOwnerUser)   _owners.push({   value: user.id, label: user.name });
                if (isManagerUser) _managers.push({ value: user.id, label: user.name });
            });
            setOwners(sortBy(uniq(_owners,   true, (u: any) => u.value), 'label'));
            setManagers(sortBy(uniq(_managers, true, (u: any) => u.value), 'label'));
            setVerticals(sortBy(Object.values(_verticals), 'label'));
            setDepartments(sortBy(Object.values(_departments), 'label'));
            return;
        }

        // ── Non-escalation (Owner / Manager) ────────────────────────────────────
        companiesLoadedRef.current = true;

        const map:          any   = {};
        const _verticals:   any   = {};
        const _departments: any   = {};
        const _owners:      any[] = [];
        const _managers:    any[] = [];
        setAssociateCompanies(undefined);
        setLocations(undefined);
        setStates([]);
        setAllLocations([]);

        (departmentUsers as any[]).forEach(({ department, user_id, user }: any) => {
            const { vertical } = department;
            const { company }  = vertical;
            if (!map[company.id] && user_id === loginUser) {
                map[company.id] = { value: company.id, label: company.name, code: company.code || null };
            }
            if (!_verticals[vertical.id] && map[company.id]) {
                _verticals[vertical.id] = { value: vertical.id, label: vertical.name, company_id: company.id };
            }
            if (!_departments[department.id] && map[company.id]) {
                _departments[department.id] = {
                    value: department.id, label: department.name,
                    company_id: company.id, verticalId: vertical.id,
                };
            }
            const isOwnerUser   = Boolean(user.roles.find(({ pages }: any) => pages.includes(USER_PRIVILEGES.OWNER_DASHBOARD)));
            const isManagerUser = Boolean(user.roles.find(({ pages }: any) => pages.includes(USER_PRIVILEGES.MANAGER_DASHBOARD)));
            if (isOwnerUser)   _owners.push({   value: user.id, label: user.name, company_id: company.id });
            if (isManagerUser) _managers.push({ value: user.id, label: user.name, company_id: company.id });
        });

        const comps = sortBy(Object.values(map), 'label') as any[];
        setCompanies(comps);
        // Deduplicate owners/managers by user id, scoped to accessible companies
        setOwners(sortBy(
            uniq(_owners.filter(({ company_id }: any) => Boolean(map[company_id])), true, (u: any) => u.value),
            'label',
        ));
        setManagers(sortBy(
            uniq(_managers.filter(({ company_id }: any) => Boolean(map[company_id])), true, (u: any) => u.value),
            'label',
        ));
        setVerticals(sortBy(Object.values(_verticals), 'label'));
        setDepartments(sortBy(Object.values(_departments), 'label'));
        if (comps.length) {
            // When arriving from the dashboard, prefer the company that was selected there
            const dp = dashboardNav && dashboardNav.dashboardPayload;
            const dpCompanyId = dp && dp.company_id && dp.company_id.length === 1
                ? dp.company_id[0] : null;
            const preSelect = dpCompanyId
                ? (comps.find((c: any) => c.value === dpCompanyId) || comps[0])
                : comps[0];
            setFilters((prev: any) => ({ ...prev, company: preSelect }));
        }
    }, [fetchingDeptUsers]);

    // ── Escalation: populate company list from user_companies tree ─────────────
    useEffect(() => {
        if (!isEscalation || fetchingUserCompanies || !userCompanies) return;
        companiesLoadedRef.current = true;
        const companyOptions = sortBy(
            (userCompanies as any[]).map((c: any) => ({ value: c.id, label: c.name, code: c.code || null })),
            'label',
        );
        setCompanies(companyOptions);
        if (companyOptions.length > 0) {
            const dp = dashboardNav && dashboardNav.dashboardPayload;
            const dpCompanyId = dp && dp.company_id && dp.company_id.length === 1 ? dp.company_id[0] : null;
            const preSelect = dpCompanyId
                ? (companyOptions.find((c: any) => c.value === dpCompanyId) || companyOptions[0])
                : companyOptions[0];
            setFilters((prev: any) => ({ ...prev, company: preSelect }));
        }
    }, [fetchingUserCompanies, userCompanies]);

    // ── Escalation: derive ACs from nested tree when company changes ───────────
    useEffect(() => {
        if (!isEscalation || !(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE || !userCompanies) return;
        const comp = (userCompanies as any[]).find((c: any) => c.id === filters.company.value);
        const acOptions = sortBy(
            ((comp && comp.associate_companies) || []).map((ac: any) => ({ value: ac.id, label: ac.name, code: ac.code || null })),
            'label',
        );
        setAssociateCompanies([DEFAULT_OPTION, ...acOptions]);
        setStates([]);
        setLocations(undefined);
        setAllLocations([]);
    }, [filters.company, userCompanies]);

    // ── Escalation: derive states + locations from nested tree when AC changes ──
    useEffect(() => {
        if (!isEscalation || !(filters.associate_company && filters.associate_company.value) || filters.associate_company.value === DEFAULT_VALUE || !userCompanies) return;
        const comp = (userCompanies as any[]).find((c: any) => c.id === (filters.company || {}).value);
        const ac   = ((comp && comp.associate_companies) || []).find((a: any) => a.id === filters.associate_company.value);
        const locs = (ac && ac.locations) || [];
        const _statesMap: any = {};
        const _locOpts: any[] = [];
        locs.forEach((loc: any) => {
            if (loc.state && !_statesMap[loc.state.id])
                _statesMap[loc.state.id] = { value: loc.state.id, label: loc.state.name };
            _locOpts.push({ value: loc.location_id || loc.id, label: loc.name, code: loc.code || null, state_id: loc.state ? loc.state.id : null });
        });
        setStates([DEFAULT_OPTION, ...sortBy(Object.values(_statesMap), 'label')]);
        setAllLocations(_locOpts);
        setLocations([DEFAULT_OPTION, ..._locOpts]);
    }, [filters.associate_company, userCompanies]);

    useEffect(() => {
        if (isEscalation || fetchingAcs || !acs) return;
        setLocations(undefined);
        const sorted = sortBy(
            (acs || []).map(({ id, name, code }: any) => ({ value: id, label: name, code: code || null })),
            'label',
        );
        setAssociateCompanies([DEFAULT_OPTION, ...sorted]);
        // If we injected a synthetic AC for a dashboard-nav arrival, replace the
        // "Loading…" label with the real name now that the AC list has arrived.
        if (dashboardNav) {
            const dp = dashboardNav.dashboardPayload || {};
            if (dp.associate_company_id && dp.associate_company_id.length === 1) {
                const acId = dp.associate_company_id[0];
                const real = sorted.find((a: any) => a.value === acId);
                if (real) {
                    setFilters((prev: any) => ({ ...prev, associate_company: real }));
                }
            }
        }
    }, [fetchingAcs]);

    useEffect(() => {
        if (isEscalation || fetchingLocs || !locs) return;
        const _states: any = {};
        const _locs = (locs || []).map(({ location }: any) => {
            if (location.state && !_states[location.state.id]) {
                _states[location.state.id] = { value: location.state.id, label: location.state.name };
            }
            return { value: location.id, label: location.name, code: location.code || null, state_id: location.state ? location.state.id : null };
        });
        setAllLocations(_locs);
        setStates([DEFAULT_OPTION, ...sortBy(Object.values(_states), 'label')]);
        setLocations([DEFAULT_OPTION, ..._locs]);
        // After locations load, replace any synthetic state/location with real options
        // that include the proper label and (for location) the state linkage.
        if (dashboardNav) {
            const dp = dashboardNav.dashboardPayload || {};
            const patch: any = {};

            // State — match from dp.state_id if the user had one selected
            if (dp.state_id && dp.state_id.length === 1) {
                const realState = (_states as any)[dp.state_id[0]];
                if (realState) patch.state = realState;
            }

            // Location — for location-chart click use chart_location_id;
            // for other dashboard navs use dp.location_id if set.
            const locIdToMatch =
                (dashboardNav.kind === 'chart' &&
                 dashboardNav.chartType === 'location' &&
                 dashboardNav.chartLocationId)
                    ? dashboardNav.chartLocationId
                    : (dp.location_id && dp.location_id.length === 1 ? dp.location_id[0] : null);

            if (locIdToMatch) {
                const realLoc = _locs.find((l: any) => l.value === locIdToMatch);
                if (realLoc) {
                    patch.location = realLoc;
                    // Bonus: infer the state from this location's state_id
                    if (realLoc.state_id && (_states as any)[realLoc.state_id] && !patch.state) {
                        patch.state = (_states as any)[realLoc.state_id];
                    }
                }
            }

            if (Object.keys(patch).length) {
                setFilters((prev: any) => ({ ...prev, ...patch }));
            }
        }
    }, [fetchingLocs]);

    // ── Pre-populate filter dropdowns when arriving from a chart segment click ──
    // In dashboard-nav mode buildListPayload() ignores filters.* and uses
    // dashboardNav directly, so these are purely cosmetic — they let the user
    // see *what* is filtered without breaking correctness.
    //
    // Limitation: location belongs to an associate company (AC), but the chart
    // row data doesn't carry AC or state IDs.  We can show the location name
    // (synthetic option) and, if the dashboard payload already had a specific AC
    // or state selected, we show those too.  If no AC was filtered on the
    // dashboard the AC/state dropdowns remain "All".
    useEffect(() => {
        if (!dashboardNav) return;
        const dp = dashboardNav.dashboardPayload || {};
        const patch: any = {};

        // Associate company — only if the dashboard payload had exactly one AC selected
        if (dp.associate_company_id && dp.associate_company_id.length === 1) {
            patch.associate_company = { value: dp.associate_company_id[0], label: 'Loading…' };
        }

        // State — only if the dashboard payload had exactly one state selected
        if (dp.state_id && dp.state_id.length === 1) {
            patch.state = { value: dp.state_id[0], label: 'Loading…' };
        }

        // Location — for location-chart clicks we have the name from the nav state
        if (
            dashboardNav.kind === 'chart' &&
            dashboardNav.chartType === 'location' &&
            dashboardNav.chartLocationId
        ) {
            patch.location = {
                value: dashboardNav.chartLocationId,
                label: dashboardNav.chartLocationName || ('Location ' + dashboardNav.chartLocationId),
            };
        }

        if (Object.keys(patch).length) {
            setFilters((prev: any) => ({ ...prev, ...patch }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally empty — run once on mount

    // Note: More Filters is a full-screen overlay drawer — outside-click is
    // handled by the overlay's onClick. The old document mousedown listener
    // was removed because react-select portals render at body level (outside
    // moreRef's DOM tree), causing every Select option click to look like an
    // "outside click" and close the drawer immediately.

    // Scroll table back to top whenever the page number changes
    useEffect(() => {
        if (tableScrollRef.current) {
            tableScrollRef.current.scrollTop = 0;
        }
    }, [page]);

    // Reset to page 1 when filters change
    useEffect(() => { setPage(1); }, [
        filters.company, filters.associate_company, filters.state, filters.location,
        timeRange, selectedStatuses, selectedTab, selectedPriority, search,
    ]);

    // Clear row selection when page or any filter changes
    useEffect(() => { setSelectedRowIds(new Set()); }, [
        page, filters.company, filters.associate_company, filters.state, filters.location,
        timeRange, selectedStatuses, selectedTab, selectedPriority, search,
        dueDateFrom, dueDateTo, monthYear,
        estTypeFilter, actIdFilter, ruleIdFilter, activityIdFilter,
        ownerFilter, managerFilter,
    ]);

    // ── Search-as-you-type queries (only while popover is open) ───────────────
    const actsFilters: any = { name_cont: draftActSearch };
    if (draftEstType) actsFilters.establishment_type_eq = draftEstType;
    const { acts: actsData }:       any = useGetActs(
        { ...DEFAULT_OPTIONS_PAYLOAD, filters: actsFilters, sorts: ['name asc'] },
        showMore && draftActSearch.length > 0,
    );
    const { rules: rulesData }:     any = useGetRules(
        { ...DEFAULT_OPTIONS_PAYLOAD, filters: { name_cont: draftRuleSearch }, sorts: ['name asc'] },
        showMore && draftRuleSearch.length > 0,
    );
    const { activities: activitiesData }: any = useGetActivities(
        { ...DEFAULT_OPTIONS_PAYLOAD, filters: { name_cont: draftActivitySearch }, sorts: ['name asc'] },
        showMore && draftActivitySearch.length > 0,
    );

    // Sync draft inputs from committed values whenever the popover opens
    useEffect(() => {
        if (showMore) {
            setActDropdownOpen(false);
            setRuleDropdownOpen(false);
            setActivityDropdownOpen(false);
            setDraftMonthYear(monthYear);
            setDraftMYMonth(monthYear ? monthYear.split('-')[1] : '');
            setDraftMYYear(monthYear  ? monthYear.split('-')[0] : '');
            setDraftDueDateFrom(dueDateFrom);
            setDraftDueDateTo(dueDateTo);
            setDraftEstType(estTypeFilter);
            setDraftActId(actIdFilter);
            setDraftActSearch(actIdFilter ? actNameFilter : '');
            setDraftRuleId(ruleIdFilter);
            setDraftRuleSearch(ruleIdFilter ? ruleNameFilter : '');
            setDraftActivityId(activityIdFilter);
            setDraftActivitySearch(activityIdFilter ? activityNameFilter : '');
            setDraftOwner(ownerFilter ? (owners.find((o: any) => o.value === ownerFilter) || null) : null);
            setDraftManager(managerFilter ? (managers.find((m: any) => m.value === managerFilter) || null) : null);
        }
    }, [showMore]);

    // ── Build list API payload ─────────────────────────────────────────────────

    function buildListPayload() {
        const { company, associate_company, state, location } = filtersRef.current;

        // ── If we arrived from the dashboard (penalty card or KPI card) ─────────
        if (dashboardNav) {
            const dp = dashboardNav.dashboardPayload;

            // Calendar navigation: the calendar API does NOT filter by financial
            // year — it only filters by due_date + company. Adding start_date
            // range would exclude compliances whose start_date falls outside the
            // current FY, producing a count mismatch. Skip it for calendar kind.
            const f: any = dashboardNav.kind === 'calendar' ? {} : (function() {
                // Dashboard sends year_eq for Full Year, financial_year for Q1-Q4/past FYs.
                // Listing uses the same year_eq filter (compliances.year column) so counts match.
                if (dp.year_eq) {
                    return { year_eq: dp.year_eq };
                }
                const dateRange = dp.financial_year
                    ? fyToDateRange(dp.financial_year, dp.quarter || '')
                    : fyToDateRange(TIME_RANGE_OPTIONS[0].startDate.slice(0, 7), '');
                return {
                    start_date_gteq: dateRange.startDate,
                    start_date_lteq: dateRange.endDate,
                };
            }());

            // Company / location from dashboard filters
            if (dp.company_id && dp.company_id.length === 1) {
                f.company_id_eq = dp.company_id[0];
            } else if (dp.company_id && dp.company_id.length > 1) {
                f.company_id_in = dp.company_id;
            }
            if (dp.associate_company_id && dp.associate_company_id.length)
                f.associate_company_id_eq = dp.associate_company_id[0];
            if (dp.state_id && dp.state_id.length)
                f.location_city_state_id_eq = dp.state_id[0];
            if (dp.location_id && dp.location_id.length)
                f.location_id_eq = dp.location_id[0];
            if (dp.due_date)       f.due_date_lteq              = dp.due_date;
            if (dp.activity_type)  f.activity_activity_type_eq  = dp.activity_type;
            // Owner / Manager selected in the dashboard More Filters
            if (dp.owner_id   && dp.owner_id.length)   f.compliance_owner_id_eq   = dp.owner_id[0];
            if (dp.manager_id && dp.manager_id.length) f.compliance_manager_id_eq = dp.manager_id[0];

            const payload: any = {
                page,
                per: pageSize,
                sorts: [`${toApiSortCol(sortCol)} ${sortDir}`],
                filters: f,
                include:  INCLUDE_ASSOCIATION_COLUMNS,
                includes: INCLUDES_ASSOCIATIONS,
            };
            if (dp.month_year) {
                // month_year supersedes the FY date range — drop whatever was set above
                delete f.start_date_gteq;
                delete f.start_date_lteq;
                delete f.year_eq;
                f.month_eq = dayjs(dp.month_year + '-01').format('MMMM');
                f.year_eq  = parseInt(dp.month_year.split('-')[0], 10);
            }

            // Penalty-specific scope — always pre-filter to Non Compliant
            if (dashboardNav.kind === 'penalty') {
                const scopes: any = {};
                if (dashboardNav.penaltyType === 'financial_exposure')  scopes.with_financial_exposure  = true;
                if (dashboardNav.penaltyType === 'imprisonment_risk')   scopes.with_imprisonment_risk   = true;
                if (dashboardNav.penaltyType === 'license_revocation')  scopes.with_license_revocation  = true;
                payload.scopes = scopes;
                // All three penalty cards are subsets of Non Compliant activities
                f.status_eq = 'NonCompliant';
            }

            // KPI status filter (null = no filter → all statuses = Total Activities)
            if (dashboardNav.kind === 'kpi' && dashboardNav.statusFilter) {
                f.status_eq = dashboardNav.statusFilter;
            }

            // Chart segment filters
            if (dashboardNav.kind === 'chart') {
                // Status from clicked colour segment.
                // 'Other' means activities NOT in the three chart statuses
                // (Rejected, Approval Pending / Submitted, Recalled).
                if (dashboardNav.chartStatus) {
                    if (dashboardNav.chartStatus === 'Other') {
                        f.status_in = ['Submitted', 'Rejected', 'Recalled'];
                    } else {
                        f.status_eq = dashboardNav.chartStatus;
                    }
                }
                // Category chart → activity type (overrides dp.activity_type tab if any)
                if (dashboardNav.chartType === 'category' && dashboardNav.chartActivityType) {
                    f.activity_activity_type_eq = dashboardNav.chartActivityType;
                }
                // Location chart → location (overrides dp.location_id if any)
                if (dashboardNav.chartType === 'location' && dashboardNav.chartLocationId) {
                    f.location_id_eq = dashboardNav.chartLocationId;
                }
            }

            // Owner Accountability cell filters (manager login)
            if (dashboardNav.kind === 'owner') {
                if (dashboardNav.ownerId) {
                    f.compliance_owner_id_eq = dashboardNav.ownerId;
                }
                if (dashboardNav.ownerStatusFilter) {
                    f.status_eq = dashboardNav.ownerStatusFilter;
                }
            }

            // Manager Accountability cell filters (owner login)
            if (dashboardNav.kind === 'manager') {
                if (dashboardNav.managerId) {
                    f.compliance_manager_id_eq = dashboardNav.managerId;
                }
                if (dashboardNav.managerStatusFilter) {
                    f.status_eq = dashboardNav.managerStatusFilter;
                }
            }

            // Pairs Accountability cell filters (escalation login) — both owner AND manager
            if (dashboardNav.kind === 'pair') {
                if (dashboardNav.pairOwnerId) {
                    f.compliance_owner_id_eq = dashboardNav.pairOwnerId;
                }
                if (dashboardNav.pairManagerId) {
                    f.compliance_manager_id_eq = dashboardNav.pairManagerId;
                }
                if (dashboardNav.pairStatusFilter) {
                    f.status_eq = dashboardNav.pairStatusFilter;
                }
            }

            // Calendar "View More" — filter by the exact selected date + the same
            // four statuses the calendar displays (Upcoming, Non-Compliant,
            // Approval Pending, Rejected).  Compliant and Recalled are excluded
            // because the calendar never shows them.
            if (dashboardNav.kind === 'calendar' && dashboardNav.calendarDate) {
                f.due_date_gteq = dashboardNav.calendarDate;
                f.due_date_lteq = dashboardNav.calendarDate;
                f.status_in = ['Due', 'NonCompliant', 'Submitted', 'Rejected'];
            }

            // Ensure the logged-in owner/manager's own ID is always scoped in
            // (setUserDetailsInFilters is a no-op when the key is already set)
            payload.filters = setUserDetailsInFilters(payload.filters);
            return payload;
        }

        // ── Normal listing payload ─────────────────────────────────────────────
        const tr = TIME_RANGE_OPTIONS.find((o) => o.value === timeRange) || DEFAULT_TIME_RANGE;

        // Full Year → year_eq (filters by compliances.year column, same as dashboard).
        // Q1-Q4 and past FYs → start_date range (quarter/past-FY records use start_date).
        const f: any = tr.value === 'full'
            ? { year_eq: getCurrentFYStartYear() }
            : { start_date_gteq: tr.startDate, start_date_lteq: tr.endDate };

        // Company
        if (company && company.value && company.value !== DEFAULT_VALUE) {
            f.company_id_eq = company.value;
        } else {
            const ids = companies
                .filter((c: any) => c.value && c.value !== DEFAULT_VALUE)
                .map((c: any) => c.value);
            if (ids.length) f.company_id_in = ids;
        }

        if (associate_company && associate_company.value && associate_company.value !== DEFAULT_VALUE)
            f.associate_company_id_eq = associate_company.value;
        if (state && state.value && state.value !== DEFAULT_VALUE)
            f.location_city_state_id_eq = state.value;
        if (location && location.value && location.value !== DEFAULT_VALUE)
            f.location_id_eq = location.value;

        // Optional filters — multi-select: status_in when one or more pills active
        var _activeStatuses = Object.keys(selectedStatuses).filter(function(k) { return selectedStatuses[k]; });
        if (_activeStatuses.length > 0) f.status_in = _activeStatuses;
        if (selectedTab !== 'All')      f.activity_activity_type_eq = selectedTab;
        // Priority is frontend-computed from due_date — convert selection to a date range.
        // Applied before the More Filters due date so that an explicit due date range overrides.
        if (selectedPriority === 'Critical') {
            // ≤15 days left (includes overdue)
            f.due_date_lteq = dayjs().add(15, 'day').format('YYYY-MM-DD');
        } else if (selectedPriority === 'Medium') {
            // 16–20 days left
            f.due_date_gteq = dayjs().add(16, 'day').format('YYYY-MM-DD');
            f.due_date_lteq = dayjs().add(20, 'day').format('YYYY-MM-DD');
        } else if (selectedPriority === 'Low') {
            // >20 days left
            f.due_date_gteq = dayjs().add(21, 'day').format('YYYY-MM-DD');
        }
        if (search)                     f.activity_name_cont = search;
        if (dueDateFrom)                f.due_date_gteq = dueDateFrom;
        if (dueDateTo)                  f.due_date_lteq = dueDateTo;
        // Advanced More Filters
        if (actIdFilter)        f.act_id_eq = actIdFilter;
        if (ruleIdFilter)       f.rule_id_eq = ruleIdFilter;
        if (activityIdFilter)   f.activity_id_eq = activityIdFilter;
        if (ownerFilter)       f.compliance_owner_id_eq   = ownerFilter;
        if (managerFilter)     f.compliance_manager_id_eq = managerFilter;
        if (verticalFilter)    f.vertical_id_eq           = verticalFilter;
        if (departmentFilter)  f.department_id_eq         = departmentFilter;

        // Ensure the logged-in owner/manager's own ID is always scoped in
        // (setUserDetailsInFilters is a no-op when the key is already set)
        var scopedFilters = setUserDetailsInFilters(f);

        const payload: any = {
            page,
            per: pageSize,
            sorts: [`${toApiSortCol(sortCol)} ${sortDir}`],
            filters: scopedFilters,
            include:  INCLUDE_ASSOCIATION_COLUMNS,
            includes: INCLUDES_ASSOCIATIONS,
        };
        if (monthYear) {
            // A specific month+year selection supersedes the time-range entirely.
            // Drop whatever the time-range block added so they are never sent together.
            delete payload.filters.start_date_gteq;
            delete payload.filters.start_date_lteq;
            delete payload.filters.year_eq;          // clear FY year_eq if Full Year was active
            payload.filters.month_eq = dayjs(monthYear + '-01').format('MMMM');
            payload.filters.year_eq  = parseInt(monthYear.split('-')[0], 10);
        }
        return payload;
    }

    const listPayload = buildListPayload();

    // ── Export filename builder ────────────────────────────────────────────────
    // Segments (in order, joined by -): {company_code}-{ac_code}-{loc_code}-{date_segment}
    // Each segment is skipped when its filter is not selected / has no code.
    // Date priority: from_date+to_date → start_date_gteq+start_date_lteq → month_eq+year_eq
    function buildExportFileName(): string {
        const segments: string[] = [];

        const co  = filters.company;
        const ac  = filters.associate_company;
        const loc = filters.location;
        if (co  && co.value  && co.value  !== DEFAULT_VALUE && co.code)  segments.push(co.code);
        if (ac  && ac.value  && ac.value  !== DEFAULT_VALUE && ac.code)  segments.push(ac.code);
        if (loc && loc.value && loc.value !== DEFAULT_VALUE && loc.code) segments.push(loc.code);

        // Date segment helper — formats a YYYY-MM-DD pair as MmmYYYY or MmmYYYY-MmmYYYY
        function dateRange(from: string, to: string): string {
            const f = dayjs(from).format('MMMYYYY'); // e.g. Aug2026
            const t = dayjs(to).format('MMMYYYY');   // e.g. Dec2026
            return f === t ? f : `${f}-${t}`;
        }

        const lf = listPayload.filters;
        if (lf.from_date && lf.to_date) {
            // Priority 1: explicit custom date range
            segments.push(dateRange(lf.from_date, lf.to_date));
        } else if (lf.start_date_gteq && lf.start_date_lteq) {
            // Priority 2: time-range / quarter / past-FY
            segments.push(dateRange(lf.start_date_gteq, lf.start_date_lteq));
        } else if (lf.month_eq && lf.year_eq) {
            // Priority 3: specific month+year (month_eq = full name e.g. "August")
            const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const mi = MONTHS.indexOf(lf.month_eq);
            if (mi !== -1) {
                segments.push(dayjs(new Date(lf.year_eq, mi, 1)).format('MMMYYYY'));
            }
        }

        return segments.length ? `${segments.join('-')}.xlsx` : 'compliances.xlsx';
    }

    // For escalation role, block the API until a real company is selected.
    // A user with no companies assigned stays on DEFAULT_VALUE; the API must
    // not fire without a company_id or it returns data for every company.
    const escalationReady = !isEscalation || (
        filters.company != null &&
        filters.company.value != null &&
        filters.company.value !== DEFAULT_VALUE
    );
    const enabled = escalationReady && (dashboardNav ? true : Boolean(listPayload.filters.start_date_lteq || listPayload.filters.year_eq));

    const { activities, total, isFetching, refetch } =
        useGetAllComplianceActivities(listPayload, enabled);

    // ── Counts base payload for status pill counts ─────────────────────────────
    // Use a state (not useMemo) so the last known correct payload is preserved
    // when dashboardNav is cleared (pill click) BEFORE local filters finish loading.
    // Race condition fixed: we only switch to local-state path once company is known.
    const [countsBasePayload, setCountsBasePayload] = useState<ComplianceDashboardPayload>(() => {
        // Initialise from dashboard payload when arriving from dashboard.
        const dp = navState && navState.dashboard_payload;
        if (dp) return dp;
        // No dashboard context — start with current FY as a safe default.
        return { year_eq: getCurrentFYStartYear() };
    });

    // countsReady gates the dashboard_score_cards hook so it never fires before
    // the payload has been properly built from filters + time range.
    // – Dashboard redirect: payload is already correct from navState, so ready = true.
    // – Direct / fresh load:  ready = false until the first useEffect cycle below.
    const [countsReady, setCountsReady] = useState(() =>
        Boolean(navState && navState.dashboard_payload)
    );

    // ── One-shot: clear history state so a page refresh doesn't re-apply filters ─
    // window.history.state persists through hard refreshes. Without this, refreshing
    // the listing URL re-mounts the component with the same navState, causing every
    // useState initialiser above to re-read dashboard_payload and restore the filters
    // that should only apply on the first navigation from the dashboard.
    // useEffect always runs AFTER all useState initialisers have been called, so
    // every value derived from navState is already captured before we clear.
    useEffect(() => {
        if (navState) {
            window.history.replaceState(null, '', window.location.href);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // empty deps — intentionally fire once on mount only

    useEffect(() => {
        if (dashboardNav && dashboardNav.dashboardPayload) {
            // Coming from / still in dashboard context — use exact dashboard payload,
            // but also inject the specific owner/manager from accountability clicks so
            // dashboard_score_cards counts match the listing scope.
            const base: ComplianceDashboardPayload = { ...dashboardNav.dashboardPayload };
            if (dashboardNav.kind === 'owner' && dashboardNav.ownerId)
                base.owner_id = [dashboardNav.ownerId];
            if (dashboardNav.kind === 'manager' && dashboardNav.managerId)
                base.manager_id = [dashboardNav.managerId];
            if (dashboardNav.kind === 'pair') {
                if (dashboardNav.pairOwnerId)   base.owner_id   = [dashboardNav.pairOwnerId];
                if (dashboardNav.pairManagerId) base.manager_id = [dashboardNav.pairManagerId];
            }
            setCountsBasePayload(base);
            setCountsReady(true);
            return;
        }
        // Local listing: build payload from current filter state.
        // Time range is always included; company is only added when explicitly chosen.
        const p: ComplianceDashboardPayload = {};
        const { company, associate_company, state, location } = filters;
        if (company && company.value && company.value !== DEFAULT_VALUE)
            p.company_id = [company.value];
        if (associate_company && associate_company.value && associate_company.value !== DEFAULT_VALUE)
            p.associate_company_id = [associate_company.value];
        if (state && state.value && state.value !== DEFAULT_VALUE)
            p.state_id = [state.value];
        if (location && location.value && location.value !== DEFAULT_VALUE)
            p.location_id = [location.value];
        const tr = TIME_RANGE_OPTIONS.find((o: any) => o.value === timeRange) || DEFAULT_TIME_RANGE;
        if (tr.value === 'full') {
            p.year_eq = getCurrentFYStartYear();
        } else if (tr.value && String(tr.value).startsWith('fy_')) {
            p.financial_year = String(tr.value).replace('fy_', '');
        } else if (['q1','q2','q3','q4'].indexOf(tr.value) !== -1) {
            p.quarter = tr.value;
        }
        if (selectedTab && selectedTab !== 'All') p.activity_type = selectedTab;
        setCountsBasePayload(p);
        setCountsReady(true);
    }, [dashboardNav, filters, timeRange, selectedTab]);

    // Inject owner_id / manager_id so dashboard_score_cards is scoped the same
    // way get_all is scoped via setUserDetailsInFilters.
    // – Owner login  → always send owner_id  = [this user]
    // – Manager login → always send manager_id = [this user]
    // – Escalation   → no auto-inject (they select owner/manager explicitly)
    // If the base payload already carries the field (e.g. from a dashboard redirect
    // that explicitly set it), we leave it as-is.
    const countsPayloadWithUser = useMemo<ComplianceDashboardPayload>(() => {
        const { user_id } = getUserDetails();
        const p = { ...countsBasePayload };
        if (isOwner   && !p.owner_id)   p.owner_id   = [user_id];
        if (isManager && !p.manager_id) p.manager_id = [user_id];
        return p;
    }, [countsBasePayload, isOwner, isManager]);

    // Only fire score cards once the payload is confirmed from actual filters —
    // prevents a premature call on the first render with an incomplete payload.
    const { scoreCards: pillScoreCards, refetch: refetchPillCounts } =
        useGetComplianceDashboardScoreCards(countsPayloadWithUser, enabled && countsReady);

    // ── Change handlers ────────────────────────────────────────────────────────

    function handleCompanyChange(opt: any) {
        setDashboardNav(null);
        const isAll = !opt || opt.value === DEFAULT_VALUE;
        setAssociateCompanies(undefined);
        setLocations(undefined);
        setStates([]);
        setAllLocations([]);
        // Reset More Filters — company change invalidates any cross-company filter context
        setMonthYear(''); setDueDateFrom(''); setDueDateTo('');
        setEstTypeFilter('');
        setActIdFilter(null); setActNameFilter('');
        setRuleIdFilter(null); setRuleNameFilter('');
        setActivityIdFilter(null); setActivityNameFilter('');
        setOwnerFilter(null); setManagerFilter(null); setVerticalFilter(null); setDepartmentFilter(null);
        setDraftMonthYear(''); setDraftMYMonth(''); setDraftMYYear('');
        setDraftDueDateFrom(''); setDraftDueDateTo('');
        setDraftEstType('');
        setDraftActId(null); setDraftActSearch('');
        setDraftRuleId(null); setDraftRuleSearch('');
        setDraftActivityId(null); setDraftActivitySearch('');
        setDraftOwner(null); setDraftManager(null); setDraftVertical(null); setDraftDepartment(null);
        setFilters((prev: any) => ({
            ...prev,
            company:           opt || DEFAULT_OPTION,
            associate_company: isAll ? null : DEFAULT_OPTION,
            state:             null,
            location:          null,
        }));
    }

    function handleACChange(opt: any) {
        setDashboardNav(null);
        const isAll = !opt || opt.value === DEFAULT_VALUE;
        setLocations(undefined);
        setStates([]);
        setAllLocations([]);
        setFilters((prev: any) => ({
            ...prev,
            associate_company: opt || DEFAULT_OPTION,
            state:    isAll ? null : DEFAULT_OPTION,
            location: isAll ? null : DEFAULT_OPTION,
        }));
    }

    function handleStateChange(opt: any) {
        setDashboardNav(null);
        const isAll = !opt || opt.value === DEFAULT_VALUE;
        const filtered = isAll ? allLocations : allLocations.filter((l: any) => l.state_id === opt.value);
        setLocations([DEFAULT_OPTION, ...filtered]);
        setFilters((prev: any) => ({ ...prev, state: opt || null, location: DEFAULT_OPTION }));
    }

    function handleLocationChange(opt: any) {
        setDashboardNav(null);
        setFilters((prev: any) => ({ ...prev, location: opt || DEFAULT_OPTION }));
    }

    // Map UI sort column keys to the actual DB column sent to the API.
    // Priority is a frontend-computed field derived from due_date, so sort
    // ascending/descending Priority by due_date ascending/descending.
    function toApiSortCol(col: string): string {
        return col === 'priority' ? 'due_date' : col;
    }

    function handleSort(col: string) {
        if (sortCol === col) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
    }

    function handleStatusPill(key: string) {
        setDashboardNav(null);   // exit dashboard-nav mode so the normal path picks up selectedStatuses
        setSelectedStatuses(function(prev) {
            var next: Record<string, boolean> = {};
            Object.keys(prev).forEach(function(k) { next[k] = prev[k]; });
            if (next[key]) {
                delete next[key];
            } else {
                next[key] = true;
            }
            return next;
        });
    }

    const [downloadingZip, setDownloadingZip] = useState(false);

    async function handleAllFilesDownload() {
        if (downloadingZip) return;
        // Use selected rows when any are checked, otherwise fall back to all rows on current page
        const ids = selectedRowIds.size > 0
            ? Array.from(selectedRowIds)
            : (rows || []).filter((r: any) => r.id).map((r: any) => r.id);
        if (ids.length === 0) {
            toast.warn('No activities available to download.');
            return;
        }
        setDownloadingZip(true);
        try {
            const response: any = await post('/api/v1/compliance_details/bulk_download', { compliance_ids: ids });
            const resData = (response && response.data && response.data.data) || response.data || {};
            if (resData.success && resData.file_url) {
                const fileUrl = decodeURIComponent(resData.file_url);
                downloadWithPresignedUrl(
                    `ComplianceActivities_${new Date().toISOString().split('T')[0]}.zip`,
                    fileUrl,
                );
                toast.success('ZIP file downloaded successfully');
            } else {
                toast.warn(resData.error || 'No files available for download.');
            }
        } catch (err: any) {
            const msg = (err && err.response && err.response.data && (err.response.data.error || err.response.data.message)) || 'Failed to download files.';
            toast.error(msg);
        } finally {
            setDownloadingZip(false);
        }
    }

    async function handleExport() {
        if (exporting) return;
        if (!total) {
            toast.warn('There are no compliance activities available for the selected filter criteria.');
            return;
        }
        setExporting(true);
        try {
            // Export all matching records: override page → 1 and per → total
            // so the API returns every row for the current filters, not just the
            // current page (listPayload uses per: PAGE_SIZE = 15).
            var exportPayload = Object.assign({}, listPayload, { page: 1, per: total });
            const response = await post(
                '/api/v1/compliances/export',
                { compliances: exportPayload } as any,
                null,
                true,
                { responseType: 'blob' },
            );
            var contentType = (response as any).headers && (response as any).headers['content-type'];
            if (contentType && contentType.includes('application/json')) {
                // Row count exceeded threshold — backend queued an email instead
                var json = await readBlobAsJson((response as any).data);
                toast.info((json && json.message) || 'Export is being processed. You will receive it via email shortly.');
            } else {
                downloadFileContent({
                    name: buildExportFileName(),
                    type: contentType || 'application/octet-stream',
                    content: (response as any).data,
                });
            }
        } catch {
            toast.error('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    }

    // Search: fire immediately on Enter; also debounced 400 ms after each keystroke
    function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') { setDashboardNav(null); setSearch(searchInput.trim()); }
    }
    function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
        setSearchInput(e.target.value);
    }

    // Debounced search-as-you-type: fires 400 ms after the last keystroke.
    // Skip the initial mount so we never clear dashboardNav on arrival.
    const searchEffectMounted = useRef(false);
    useEffect(function() {
        if (!searchEffectMounted.current) {
            searchEffectMounted.current = true;
            return;
        }
        var timer = setTimeout(function() {
            setDashboardNav(null);
            setSearch(searchInput.trim());
        }, 400);
        return function() { clearTimeout(timer); };
    }, [searchInput]);

    // Close priority dropdown on outside click
    useEffect(function() {
        function handler(e: MouseEvent) {
            if (priorityDropRef.current && !priorityDropRef.current.contains(e.target as Node)) {
                setPriorityDropOpen(false);
            }
        }
        document.addEventListener('mousedown', handler);
        return function() { document.removeEventListener('mousedown', handler); };
    }, []);

    // ── Pagination helpers ─────────────────────────────────────────────────────

    const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

    function paginationPages(): (number | '...')[] {
        const pages: (number | '...')[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3)               pages.push('...');
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                pages.push(i);
            }
            if (page < totalPages - 2)  pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    }

    const rows: any[] = activities || [];
    const startRow    = (page - 1) * pageSize + 1;
    const endRow      = Math.min(page * pageSize, total || 0);


    // ── Render helpers ─────────────────────────────────────────────────────────

    function renderStatusBadge(status: string) {
        const cls   = BADGE_CLASS[status]  || 'cl2-badge cl2-badge-due';
        const label = STATUS_LABEL[status] || status;
        return <span className={cls}>{label}</span>;
    }

    function renderPriority(dueDate: string | null | undefined) {
        if (!dueDate) return <span className="cl2-priority-none">—</span>;
        var today    = dayjs().startOf('day');
        var due      = dayjs(dueDate).startOf('day');
        var daysLeft = due.diff(today, 'day');
        var label: string;
        var cls: string;
        if (daysLeft <= 15) {
            label = 'Critical';
            cls   = 'cl2-priority-critical';
        } else if (daysLeft <= 20) {
            label = 'Medium';
            cls   = 'cl2-priority-medium';
        } else {
            label = 'Low';
            cls   = 'cl2-priority-low';
        }
        var daysText = daysLeft < 0
            ? Math.abs(daysLeft) + ' day' + (Math.abs(daysLeft) === 1 ? '' : 's') + ' overdue'
            : daysLeft + ' day' + (daysLeft === 1 ? '' : 's') + ' left';
        return (
            <span className="cl2-priority-wrap">
                <span className={cls}>{label}</span>
                <span className={cls + ' cl2-priority-days'}>{daysText}</span>
            </span>
        );
    }

    const labelStyle: React.CSSProperties = {
        fontSize: 12, color: '#757575', fontWeight: 500,
        fontFamily: 'Montserrat, sans-serif', marginBottom: 6, display: 'block',
    };
    const dateInputStyle: React.CSSProperties = {
        height: 38, width: '100%', border: '1px solid #e6e6e6', borderRadius: 8,
        padding: '0 12px', fontSize: 13, color: '#1e1e1e',
        fontFamily: 'Montserrat, sans-serif', background: '#fff',
        outline: 'none', cursor: 'pointer', boxSizing: 'border-box',
    };

    // ── More Filters active-filter count (used by both button badge and drawer header) ──
    // Note: estTypeFilter is intentionally excluded — it only narrows the Act
    // search dropdown and does not filter the compliance listing itself.
    const activeCount = [
        monthYear, dueDateFrom, dueDateTo,
        actIdFilter, ruleIdFilter, activityIdFilter,
        ownerFilter, managerFilter,
        verticalFilter, departmentFilter,
    ].filter(Boolean).length;

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="cl2-outer">
        <div className="cl2-wrapper">

            {/* ── Header ── */}
            <div className="cl2-header">
                <div>
                    <h1 className="cl2-header-title">Compliance Listing</h1>
                    <p className="cl2-header-subtitle">
                        Track regulatory compliance, manage audits, and stay ahead of policy deadlines.
                    </p>
                </div>
                <div className="cl2-header-actions">
                    {/* Search */}
                    <div className="cl2-search">
                        <input
                            placeholder="Search by activity name"
                            value={searchInput}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                        />
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <circle cx="7" cy="7" r="4.5" />
                            <line x1="10.5" y1="10.5" x2="14" y2="14" />
                        </svg>
                    </div>

                    {/* All Files Download — owner, manager and escalation; disabled until rows are selected */}
                    {(isOwner || isManager || isEscalation) && (
                        <button
                            className="cl2-export-btn"
                            disabled={downloadingZip || !enabled || selectedRowIds.size === 0}
                            onClick={handleAllFilesDownload}
                            title={selectedRowIds.size === 0 ? 'Select rows to download files' : undefined}
                        >
                            {downloadingZip ? (
                                'Downloading…'
                            ) : (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M2.667 10.667v1.333A1.333 1.333 0 0 0 4 13.333h8a1.333 1.333 0 0 0 1.333-1.333v-1.333M10.667 8 8 10.667M8 10.667 5.333 8M8 10.667V2.667" />
                                    </svg>
                                    {selectedRowIds.size > 0 ? `All Files (${selectedRowIds.size})` : 'All Files'}
                                </>
                            )}
                        </button>
                    )}

                    {/* Bulk Review — manager only; always visible, disabled until rows are selected */}
                    {isManager && (
                        <button
                            className="cl2-export-btn"
                            disabled={selectedRowIds.size === 0}
                            onClick={() => setBulkReviewOpen(true)}
                            title={selectedRowIds.size === 0 ? 'Select rows to bulk review' : undefined}
                        >
                            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M13.333 2H2.667A1.333 1.333 0 0 0 1.333 3.333v9.334A1.333 1.333 0 0 0 2.667 14h10.666a1.333 1.333 0 0 0 1.334-1.333V3.333A1.333 1.333 0 0 0 13.333 2zM5.333 5.333h5.334M5.333 8h5.334M5.333 10.667H8" />
                            </svg>
                            {selectedRowIds.size > 0 ? `Bulk Review (${selectedRowIds.size})` : 'Bulk Review'}
                        </button>
                    )}

                    {/* Bulk Upload — owner only; always visible, disabled until rows are selected */}
                    {isOwner && (
                        <button
                            className="cl2-export-btn"
                            disabled={selectedRowIds.size === 0}
                            onClick={() => setBulkUploadOpen(true)}
                            title={selectedRowIds.size === 0 ? 'Select rows to bulk upload' : undefined}
                        >
                            {/* Download icon flipped vertically = upload direction */}
                            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'scaleY(-1)' }}>
                                <path d="M2.667 10.667v1.333A1.333 1.333 0 0 0 4 13.333h8a1.333 1.333 0 0 0 1.333-1.333v-1.333M10.667 8 8 10.667M8 10.667 5.333 8M8 10.667V2.667" />
                            </svg>
                            {selectedRowIds.size > 0 ? `Bulk Upload (${selectedRowIds.size})` : 'Bulk Upload'}
                        </button>
                    )}

                    {/* Export */}
                    <button className="cl2-export-btn" onClick={handleExport} disabled={exporting || !enabled}>
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2.667 10.667v1.333A1.333 1.333 0 0 0 4 13.333h8a1.333 1.333 0 0 0 1.333-1.333v-1.333M10.667 8 8 10.667M8 10.667 5.333 8M8 10.667V2.667" />
                        </svg>
                        {exporting ? 'Generating…' : 'Export'}
                    </button>
                </div>
            </div>

            {/* ── Filter Card ── */}
            <div className="cl2-filter-card">

                {/* Main filter row */}
                <div className="cl2-filter-row">

                    {/* Company */}
                    <div className="cl2-filter-group">
                        <label className="cl2-filter-label">Company</label>
                        <Select
                            styles={selectStyles}
                            menuPosition="fixed"
                            options={companies}
                            value={filters.company || DEFAULT_OPTION}
                            onChange={handleCompanyChange}
                            isLoading={isEscalation ? fetchingUserCompanies : fetchingDeptUsers}
                        />
                    </div>

                    {/* Associate Company */}
                    <div className="cl2-filter-group">
                        <label className="cl2-filter-label">Associate Company</label>
                        <Select
                            styles={selectStyles}
                            menuPosition="fixed"
                            options={associateCompanies || [DEFAULT_OPTION]}
                            value={filters.associate_company || DEFAULT_OPTION}
                            onChange={handleACChange}
                            isDisabled={!associateCompanies}
                            isLoading={!isEscalation && fetchingAcs}
                        />
                    </div>

                    {/* State */}
                    <div className="cl2-filter-group">
                        <label className="cl2-filter-label">State</label>
                        <Select
                            styles={selectStyles}
                            menuPosition="fixed"
                            options={states}
                            value={filters.state || DEFAULT_OPTION}
                            onChange={handleStateChange}
                            isDisabled={states.length === 0}
                        />
                    </div>

                    {/* Location */}
                    <div className="cl2-filter-group">
                        <label className="cl2-filter-label">Location</label>
                        <Select
                            styles={selectStyles}
                            menuPosition="fixed"
                            options={locations || [DEFAULT_OPTION]}
                            value={filters.location || DEFAULT_OPTION}
                            onChange={handleLocationChange}
                            isDisabled={!locations}
                            isLoading={!isEscalation && fetchingLocs}
                        />
                    </div>

                    {/* Time Range */}
                    <div className="cl2-filter-group">
                        <label className="cl2-filter-label">Time Range</label>
                        <select
                            className="cl2-time-select"
                            value={timeRange}
                            onChange={(e) => { setDashboardNav(null); setTimeRange(e.target.value); }}
                        >
                            {TIME_RANGE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* More Filters — badge shows count of active committed filters */}
                    <div style={{ position: 'relative', alignSelf: 'flex-end' }}>
                        <button
                            className={'cl2-more-btn' + (activeCount > 0 ? ' cl2-more-btn-active' : '')}
                            onClick={() => setShowMore(!showMore)}
                        >
                            More Filters
                            {activeCount > 0 && (
                                <span className="cl2-more-badge">{activeCount}</span>
                            )}
                        </button>
                        {/* drawer rendered at root level below */}
                    </div>
                </div>

                {/* Status pills */}
                <div className="cl2-status-row">
                    <span className="cl2-status-label">Status</span>
                    {STATUS_PILLS.map((pill) => {
                        // When arriving from the calendar, highlight the 4 calendar statuses
                        // directly from dashboardNav (reliable) rather than from selectedStatuses
                        // (which depends on useState initialiser timing).
                        // Once the user clicks any pill, dashboardNav is cleared and
                        // selectedStatuses takes over for all subsequent interactions.
                        const isActive = (dashboardNav && dashboardNav.kind === 'calendar')
                            ? CALENDAR_PILL_KEYS.has(pill.key)
                            : !!selectedStatuses[pill.key];
                        const scField = PILL_COUNT_KEYS[pill.key];
                        const scEntry = pillScoreCards && scField ? (pillScoreCards as any)[scField] : null;
                        const cnt     = scEntry != null && scEntry.count != null ? scEntry.count : null;
                        const pillLabel = cnt != null
                            ? `${pill.label} (${cnt.toLocaleString('en-IN')})`
                            : pill.label;
                        return (
                            <button
                                key={pill.key}
                                className={`cl2-status-pill ${pill.cls}${isActive ? ' active' : ''}`}
                                onClick={() => handleStatusPill(pill.key)}
                            >
                                {pillLabel}
                            </button>
                        );
                    })}
                </div>

                {/* Priority active — amber banner below the pills row */}
                {selectedPriority !== 'All' && (
                    <div className="cl2-status-priority-note">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        Status counts reflect the selected time range and are not filtered by priority.
                    </div>
                )}

                {/* Activity type tabs + Priority dropdown */}
                <div className="cl2-bottom-row">
                    <div className="cl2-tabs">
                        {ACTIVITY_TABS.map((tab) => (
                            <button
                                key={tab}
                                className={`cl2-tab${selectedTab === tab ? ' active' : ''}`}
                                onClick={() => { setDashboardNav(null); setSelectedTab(tab); }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    {/* Custom priority dropdown — native <select> can't have
                        blue option-hover in Firefox; this div-based version can. */}
                    <div ref={priorityDropRef} className="cl2-priority-drop" style={{ marginBottom: 8 }}>
                        <button
                            type="button"
                            className="cl2-priority-select"
                            onClick={() => setPriorityDropOpen((o) => !o)}
                        >
                            Priority: {selectedPriority}
                        </button>
                        {priorityDropOpen && (
                            <div className="cl2-priority-options">
                                {PRIORITY_OPTIONS.map((p) => (
                                    <div
                                        key={p}
                                        className={`cl2-priority-option${selectedPriority === p ? ' active' : ''}`}
                                        onMouseDown={() => {
                                            setDashboardNav(null);
                                            setSelectedPriority(p);
                                            setPriorityDropOpen(false);
                                            if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
                                        }}
                                    >
                                        Priority: {p}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Penalty filter active banner (penalty redirects only) ── */}
            {dashboardNav && dashboardNav.kind === 'penalty' && (
                <div className="cl2-penalty-banner">
                    <span className="cl2-penalty-banner-icon">
                        {dashboardNav.penaltyType === 'financial_exposure'  && '₹'}
                        {dashboardNav.penaltyType === 'imprisonment_risk'   && '⚠'}
                        {dashboardNav.penaltyType === 'license_revocation'  && '🔒'}
                    </span>
                    <span className="cl2-penalty-banner-text">
                        Filtered by <strong>{dashboardNav.label}</strong> from Compliance Dashboard
                    </span>
                    <button
                        className="cl2-penalty-banner-close"
                        title="Go back to Compliance Dashboard"
                        onClick={() => { window.history.back(); }}
                    >
                        ✕ Back to Dashboard
                    </button>
                </div>
            )}

            {/* ── Table Card ── */}
            <div className="cl2-table-card">
                <div className="cl2-table-layout">

                    {/* Single scroll area — horizontal + vertical */}
                    <div className="cl2-table-scroll-area" ref={tableScrollRef}>
                        <table className="cl2-table">
                            <colgroup>
                                <col style={{ width: 60 }} />   {/* checkbox */}
                                <col style={{ width: 190 }} />  {/* Act */}
                                <col style={{ width: 190 }} />  {/* Activity */}
                                <col style={{ width: 160 }} />  {/* Rule */}
                                <col style={{ width: 160 }} />  {/* Activity Type */}
                                <col style={{ width: 120 }} />  {/* Priority */}
                                <col style={{ width: 140 }} />  {/* Due Date */}
                                <col style={{ width: 120 }} />  {/* Month */}
                                <col style={{ width: 100 }} />  {/* Year */}
                                <col style={{ width: 140 }} />  {/* Start Date */}
                                <col style={{ width: 160 }} />  {/* Vertical */}
                                <col style={{ width: 160 }} />  {/* Department */}
                                <col style={{ width: 150 }} />  {/* Approver Due Date */}
                                <col style={{ width: 140 }} />  {/* Audited Date */}
                                <col style={{ width: 160 }} />  {/* Status (sticky) */}
                                <col style={{ width: 120 }} />  {/* Actions (sticky) */}
                            </colgroup>
                            <thead className="cl2-thead">
                                <tr>
                                    <th className="cl2-th" style={{ width: 60 }}>
                                        {canSelect && (
                                            <input
                                                type="checkbox"
                                                className="cl2-checkbox"
                                                checked={rows.length > 0 && rows.every((r: any) => selectedRowIds.has(r.id))}
                                                onChange={(e) => {
                                                    const next = new Set(selectedRowIds);
                                                    if (e.target.checked) {
                                                        rows.forEach((r: any) => next.add(r.id));
                                                    } else {
                                                        rows.forEach((r: any) => next.delete(r.id));
                                                    }
                                                    setSelectedRowIds(next);
                                                }}
                                            />
                                        )}
                                    </th>
                                    {[
                                        { label: 'Act',           col: 'act_name' },
                                        { label: 'Activity',      col: 'activity_name' },
                                        { label: 'Rule',          col: 'rule_name' },
                                        { label: 'Activity Type', col: 'activity_activity_type' },
                                        { label: 'Priority',      col: 'priority' },
                                        { label: 'Due Date',      col: 'due_date' },
                                        { label: 'Month',         col: 'due_date_month' },
                                        { label: 'Year',          col: 'due_date_year' },
                                        { label: 'Start Date',    col: 'start_date' },
                                        { label: 'Vertical',          col: 'vertical_name' },
                                        { label: 'Department',        col: 'department_name' },
                                        { label: 'Approver Due Date', col: 'approver_due_date' },
                                        { label: 'Audited Date',      col: 'audited_date' },
                                    ].map(({ label, col }) => (
                                        <th
                                            key={col}
                                            className="cl2-th"
                                            onClick={() => handleSort(col)}
                                        >
                                            <span className="cl2-th-sort">
                                                {label}
                                                <SortIcon
                                                    active={sortCol === col}
                                                    dir={sortCol === col ? sortDir : 'desc'}
                                                />
                                            </span>
                                        </th>
                                    ))}
                                    <th className="cl2-th cl2-th-status">Status</th>
                                    <th className="cl2-th cl2-th-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="cl2-tbody">
                                {isFetching ? (
                                    Array.from({ length: PAGE_SIZE }).map((_, i) => (
                                        <tr key={i} className="cl2-tr">
                                            <td className="cl2-td-check"><Skeleton w={18} /></td>
                                            {[190, 190, 160, 160, 120, 140, 120, 100, 140, 160, 160, 150, 140].map((w, j) => (
                                                <td key={j} className="cl2-td" style={{ width: w }}>
                                                    <Skeleton w="75%" />
                                                </td>
                                            ))}
                                            <td className="cl2-td cl2-td-status"><Skeleton w={80} /></td>
                                            <td className="cl2-td cl2-td-actions"><Skeleton w={60} /></td>
                                        </tr>
                                    ))
                                ) : rows.length === 0 ? null : (
                                    rows.map((row: any) => (
                                        <tr key={row.id} className="cl2-tr">
                                            <td className="cl2-td-check">
                                                {canSelect && (
                                                    <input
                                                        type="checkbox"
                                                        className="cl2-checkbox"
                                                        checked={selectedRowIds.has(row.id)}
                                                        onChange={(e) => {
                                                            const next = new Set(selectedRowIds);
                                                            if (e.target.checked) { next.add(row.id); } else { next.delete(row.id); }
                                                            setSelectedRowIds(next);
                                                        }}
                                                    />
                                                )}
                                            </td>
                                            <td className="cl2-td" title={row.act && row.act.name}>
                                                {(row.act && row.act.name) || '—'}
                                            </td>
                                            <td className="cl2-td" title={row.activity && row.activity.name}>
                                                {(row.activity && row.activity.name) || '—'}
                                            </td>
                                            <td className="cl2-td" title={row.rule && row.rule.name}>
                                                {(row.rule && row.rule.name) || '—'}
                                            </td>
                                            <td className="cl2-td">
                                                {(row.activity && row.activity.activity_type) || '—'}
                                            </td>
                                            <td className="cl2-td">
                                                {renderPriority(row.due_date)}
                                            </td>
                                            <td className="cl2-td cl2-due-date">
                                                {row.due_date ? dayjs(row.due_date).format('DD-MM-YYYY') : '—'}
                                            </td>
                                            <td className="cl2-td">
                                                {row.month || '—'}
                                            </td>
                                            <td className="cl2-td">
                                                {row.year ? String(row.year) : '—'}
                                            </td>
                                            <td className="cl2-td">
                                                {row.start_date ? dayjs(row.start_date).format('DD-MM-YYYY') : '—'}
                                            </td>
                                            <td className="cl2-td" title={(row.vertical && row.vertical.name) || ''}>
                                                {(row.vertical && row.vertical.name) || '—'}
                                            </td>
                                            <td className="cl2-td" title={(row.department && row.department.name) || ''}>
                                                {(row.department && row.department.name) || '—'}
                                            </td>
                                            <td className="cl2-td">
                                                {row.approver_due_date ? dayjs(row.approver_due_date).format('DD-MM-YYYY') : '—'}
                                            </td>
                                            <td className="cl2-td">
                                                {row.audited_date ? dayjs(row.audited_date).format('DD-MM-YYYY') : '—'}
                                            </td>
                                            {/* Status — sticky right */}
                                            <td className="cl2-td cl2-td-status">
                                                {renderStatusBadge(row.status)}
                                            </td>
                                            {/* Actions — sticky right edge */}
                                            <td className="cl2-td cl2-td-actions">
                                                <div className="cl2-td-actions-inner">
                                                    <button
                                                        className="cl2-action-btn"
                                                        title="View"
                                                        onClick={() => { setDrawerMode('view'); setDrawerActivity(row); }}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2965AD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                            <circle cx="12" cy="12" r="3" />
                                                        </svg>
                                                    </button>
                                                    {!isEscalation && (
                                                        <button
                                                            className="cl2-action-btn"
                                                            title="Edit"
                                                            onClick={() => { setDrawerMode('edit'); setDrawerActivity(row); }}
                                                        >
                                                            <img src={iconEdit} width="16" height="16" alt="Edit" />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="cl2-action-btn"
                                                        title="Download Form"
                                                        onClick={() => {
                                                            const asm = row.act_state_mapping;
                                                            if (asm && asm.file_path) {
                                                                downloadWithPresignedUrl(asm.file_name, asm.file_path);
                                                            } else {
                                                                toast.warn('No form file available for this activity');
                                                            }
                                                        }}
                                                    >
                                                        <img src={iconDownload} width="16" height="16" alt="Download" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {!isFetching && rows.length === 0 && (
                            <div className="cl2-empty">No compliance activities found.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Pagination — outside the table card, sits on the page background ── */}
            {(total || 0) > 0 && (
                    <div className="cl2-pagination">
                        {/* Left: results-per-page selector + count info */}
                        <div className="cl2-pagination-left">
                            <div className="cl2-rpp">
                                <span className="cl2-rpp-label">Results per page</span>
                                <select
                                    className="cl2-rpp-select"
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                >
                                    {PAGE_SIZE_OPTIONS.map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                            <span className="cl2-pagination-info">
                                Showing {startRow}–{endRow} of {total} results
                            </span>
                        </div>
                        <div className="cl2-pagination-controls">
                            <button
                                className="cl2-page-btn"
                                disabled={page <= 1 || isFetching}
                                onClick={() => setPage((p) => p - 1)}
                            >
                                Previous
                            </button>
                            {paginationPages().map((p, i) =>
                                p === '...' ? (
                                    <span key={`e-${i}`} className="cl2-page-ellipsis">…</span>
                                ) : (
                                    <button
                                        key={p}
                                        className={`cl2-page-btn${page === p ? ' active' : ''}`}
                                        disabled={isFetching}
                                        onClick={() => setPage(p as number)}
                                    >
                                        {p}
                                    </button>
                                ),
                            )}
                            <button
                                className="cl2-page-btn"
                                disabled={page >= totalPages || isFetching}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

        </div>

        {/* ── Activity Detail Drawer ── */}
        <ComplianceActivityDrawer
            activity={drawerActivity}
            initialMode={drawerMode}
            onClose={() => setDrawerActivity(null)}
            onRefresh={() => { refetch(); refetchPillCounts(); }}
        />

        {/* ── Bulk Upload Modal (owner) ── */}
        {bulkUploadOpen && (
            <ComplianceBulkUploadModal
                selectedItems={rows.filter((r: any) => selectedRowIds.has(r.id))}
                onClose={(refresh?: boolean) => {
                    setBulkUploadOpen(false);
                    if (refresh) {
                        setSelectedRowIds(new Set());
                        refetch();
                        refetchPillCounts();
                    }
                }}
            />
        )}

        {/* ── Bulk Review Modal (manager) ── */}
        {bulkReviewOpen && (
            <ComplianceBulkReviewModal
                selectedItems={rows.filter((r: any) => selectedRowIds.has(r.id))}
                onClose={(refresh?: boolean) => {
                    setBulkReviewOpen(false);
                    if (refresh) {
                        setSelectedRowIds(new Set());
                        refetch();
                        refetchPillCounts();
                    }
                }}
            />
        )}

        {/* ── More Filters Drawer ── */}
        {showMore && (
            <div className="cl2-mfd-overlay" onClick={() => setShowMore(false)}>
                <div className="cl2-mfd-drawer" onClick={(e) => e.stopPropagation()}>

                    {/* Header — matches standard drawer header pattern (Location Audit / License Management) */}
                    <div className="cl2-mfd-header">
                        <div className="cl2-mfd-header-left">
                            <span className="cl2-mfd-title">More Filters</span>
                            {activeCount > 0 && (
                                <span className="cl2-mfd-active-count">{activeCount} active</span>
                            )}
                        </div>
                        <button className="cl2-mfd-close" onClick={() => setShowMore(false)} title="Close">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    {/* Body — 2-column grid for most fields, full-width for typeahead searches */}
                    <div className="cl2-mfd-body">

                        {/* Row: Vertical | Department */}
                        <div className="cl2-mfd-grid-2">
                            <div className="cl2-mfd-field">
                                <label className="cl2-mfd-label">Vertical</label>
                                <Select
                                    menuPosition="fixed"
                                    options={[{ value: null, label: 'All' }, ...verticals.filter(({ company_id }: any) => {
                                        const company = filters.company;
                                        return !company || company.value === DEFAULT_VALUE ? true : company_id === company.value;
                                    })]}
                                    value={draftVertical || { value: null, label: 'All' }}
                                    onChange={(opt: any) => {
                                        setDraftVertical(opt && opt.value ? opt : null);
                                        setDraftDepartment(null);
                                    }}
                                    isClearable={false}
                                    styles={selectStyles}
                                />
                            </div>
                            <div className="cl2-mfd-field">
                                <label className="cl2-mfd-label">Department</label>
                                <Select
                                    menuPosition="fixed"
                                    options={[{ value: null, label: 'All' }, ...departments.filter(({ company_id, verticalId }: any) => {
                                        const company = filters.company;
                                        if (draftVertical && draftVertical.value) return verticalId === draftVertical.value;
                                        if (company && company.value && company.value !== DEFAULT_VALUE) return company_id === company.value;
                                        return true;
                                    })]}
                                    value={draftDepartment || { value: null, label: 'All' }}
                                    onChange={(opt: any) => setDraftDepartment(opt && opt.value ? opt : null)}
                                    isClearable={false}
                                    styles={selectStyles}
                                />
                            </div>
                        </div>

                        {/* Owner dropdown — visible to managers and escalation (to filter by owner)
                            Manager dropdown — visible to owners and escalation (to filter by manager)
                            Escalation: both shown side-by-side; owner/manager: only one shown */}
                        {(isManager || isOwner || isEscalation) && (
                            <div className={(isManager && isOwner) || isEscalation ? 'cl2-mfd-grid-2' : 'cl2-mfd-field'}>
                                {(isManager || isEscalation) && (
                                    <div className={(isManager && isOwner) || isEscalation ? 'cl2-mfd-field' : ''}>
                                        <label className="cl2-mfd-label">Owner</label>
                                        <Select
                                            menuPosition="fixed"
                                            options={[{ value: null, label: 'All' }, ...owners]}
                                            value={draftOwner || { value: null, label: 'All' }}
                                            onChange={(opt: any) => setDraftOwner(opt && opt.value ? opt : null)}
                                            isClearable={false}
                                            isDisabled={isEscalation && (!(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE)}
                                            styles={selectStyles}
                                        />
                                    </div>
                                )}
                                {(isOwner || isEscalation) && (
                                    <div className={(isManager && isOwner) || isEscalation ? 'cl2-mfd-field' : ''}>
                                        <label className="cl2-mfd-label">Manager</label>
                                        <Select
                                            menuPosition="fixed"
                                            options={[{ value: null, label: 'All' }, ...managers]}
                                            value={draftManager || { value: null, label: 'All' }}
                                            onChange={(opt: any) => setDraftManager(opt && opt.value ? opt : null)}
                                            isClearable={false}
                                            isDisabled={isEscalation && (!(filters.company && filters.company.value) || filters.company.value === DEFAULT_VALUE)}
                                            styles={selectStyles}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Row: Month & Year */}
                        <div className="cl2-mfd-field">
                            <label className="cl2-mfd-label">Month &amp; Year</label>
                            {/* Two <select> dropdowns — <input type="month"> is unsupported
                                in Firefox / non-Chromium browsers */}
                            <div className="cl2-mfd-month-row">
                                <select
                                    className="cl2-mfd-select"
                                    value={draftMYMonth}
                                    onChange={(e) => {
                                        const m = e.target.value;
                                        setDraftMYMonth(m);
                                        setDraftMonthYear((draftMYYear && m) ? draftMYYear + '-' + m : '');
                                    }}
                                >
                                    <option value="">Month</option>
                                    {MONTH_OPTIONS.map((mo) => (
                                        <option key={mo.value} value={mo.value}>{mo.label}</option>
                                    ))}
                                </select>
                                <select
                                    className="cl2-mfd-select"
                                    value={draftMYYear}
                                    onChange={(e) => {
                                        const y = e.target.value;
                                        setDraftMYYear(y);
                                        setDraftMonthYear((y && draftMYMonth) ? y + '-' + draftMYMonth : '');
                                    }}
                                >
                                    <option value="">Year</option>
                                    {buildMonthYearOptions().map((y) => (
                                        <option key={y} value={String(y)}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Row: Due Date From | Due Date To */}
                        <div className="cl2-mfd-grid-2">
                            <div className="cl2-mfd-field">
                                <label className="cl2-mfd-label">Due Date From</label>
                                <input
                                    type="date"
                                    className="cl2-mfd-input"
                                    value={draftDueDateFrom}
                                    onChange={(e) => setDraftDueDateFrom(e.target.value)}
                                />
                            </div>
                            <div className="cl2-mfd-field">
                                <label className="cl2-mfd-label">Due Date To</label>
                                <input
                                    type="date"
                                    className="cl2-mfd-input"
                                    value={draftDueDateTo}
                                    onChange={(e) => setDraftDueDateTo(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="cl2-mfd-divider" />

                        {/* Establishment Type — full width */}
                        <div className="cl2-mfd-field">
                            <label className="cl2-mfd-label">Establishment Type</label>
                            <select
                                className="cl2-mfd-select"
                                value={draftEstType}
                                onChange={(e) => {
                                    setDraftEstType(e.target.value);
                                    setDraftActId(null);
                                    setDraftActSearch('');
                                }}
                            >
                                <option value="">All Types</option>
                                {['BOCW', 'CLRA', 'Factory', 'ISM', 'Shops'].map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                            <p className="cl2-mfd-helper">
                                Narrows the <strong>Act</strong> search options below. Does not filter the compliance listing directly.
                            </p>
                        </div>

                        {/* Act search — full width with typeahead */}
                        <div className="cl2-mfd-field">
                            <label className="cl2-mfd-label">
                                Act{draftEstType ? ' (' + draftEstType + ')' : ''}
                            </label>
                            <input
                                type="text"
                                className="cl2-mfd-input"
                                placeholder="Start typing to search…"
                                value={draftActSearch}
                                onChange={(e) => {
                                    // User actively typed / deleted → clear committed id and search
                                    setDraftActSearch(e.target.value);
                                    setDraftActId(null);
                                    setActDropdownOpen(true);
                                }}
                                onClick={() => {
                                    // If a value is already selected, clear it so user can
                                    // start typing a fresh search immediately.
                                    if (draftActId) {
                                        setDraftActId(null);
                                        setDraftActSearch('');
                                    }
                                    setActDropdownOpen(true);
                                }}
                                onBlur={() => {
                                    setActDropdownOpen(false);
                                    if (!draftActId) setDraftActSearch('');
                                }}
                            />
                            {actDropdownOpen && draftActSearch.length > 0 && actsData && actsData.length > 0 && (
                                <div className="cl2-mfd-typeahead">
                                    {actsData.map((act: any) => (
                                        <div
                                            key={act.id}
                                            className="cl2-mfd-typeahead-item"
                                            title={act.name}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                setDraftActId(act.id);
                                                setDraftActSearch(act.name);
                                                setActDropdownOpen(false);
                                            }}
                                        >
                                            <span className="cl2-mfd-typeahead-name">{act.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Rule search — full width with typeahead */}
                        <div className="cl2-mfd-field">
                            <label className="cl2-mfd-label">Rule</label>
                            <input
                                type="text"
                                className="cl2-mfd-input"
                                placeholder="Start typing to search…"
                                value={draftRuleSearch}
                                onChange={(e) => {
                                    setDraftRuleSearch(e.target.value);
                                    setDraftRuleId(null);
                                    setRuleDropdownOpen(true);
                                }}
                                onClick={() => {
                                    if (draftRuleId) {
                                        setDraftRuleId(null);
                                        setDraftRuleSearch('');
                                    }
                                    setRuleDropdownOpen(true);
                                }}
                                onBlur={() => {
                                    setRuleDropdownOpen(false);
                                    if (!draftRuleId) setDraftRuleSearch('');
                                }}
                            />
                            {ruleDropdownOpen && draftRuleSearch.length > 0 && rulesData && rulesData.length > 0 && (
                                <div className="cl2-mfd-typeahead">
                                    {rulesData.map((rule: any) => (
                                        <div
                                            key={rule.id}
                                            className="cl2-mfd-typeahead-item"
                                            title={rule.name}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                setDraftRuleId(rule.id);
                                                setDraftRuleSearch(rule.name);
                                                setRuleDropdownOpen(false);
                                            }}
                                        >
                                            <span className="cl2-mfd-typeahead-name">{rule.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Activity search — full width with typeahead */}
                        <div className="cl2-mfd-field">
                            <label className="cl2-mfd-label">Activity</label>
                            <input
                                type="text"
                                className="cl2-mfd-input"
                                placeholder="Start typing to search…"
                                value={draftActivitySearch}
                                onChange={(e) => {
                                    setDraftActivitySearch(e.target.value);
                                    setDraftActivityId(null);
                                    setActivityDropdownOpen(true);
                                }}
                                onClick={() => {
                                    if (draftActivityId) {
                                        setDraftActivityId(null);
                                        setDraftActivitySearch('');
                                    }
                                    setActivityDropdownOpen(true);
                                }}
                                onBlur={() => {
                                    setActivityDropdownOpen(false);
                                    if (!draftActivityId) setDraftActivitySearch('');
                                }}
                            />
                            {activityDropdownOpen && draftActivitySearch.length > 0 && activitiesData && activitiesData.length > 0 && (
                                <div className="cl2-mfd-typeahead">
                                    {activitiesData.map((act: any) => (
                                        <div
                                            key={act.id}
                                            className="cl2-mfd-typeahead-item"
                                            title={act.name}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                setDraftActivityId(act.id);
                                                setDraftActivitySearch(act.name);
                                                setActivityDropdownOpen(false);
                                            }}
                                        >
                                            <span className="cl2-mfd-typeahead-name">{act.name}</span>
                                            {act.activity_type && (
                                                <span className="cl2-mfd-typeahead-tag">{act.activity_type}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>{/* /cl2-mfd-body */}

                    {/* Footer */}
                    <div className="cl2-mfd-footer">
                        <button
                            className="cl2-mfd-btn cl2-mfd-btn-clear"
                            onClick={() => {
                                setShowMore(false);
                                setDashboardNav(null);
                                setDraftMonthYear(''); setDraftMYMonth(''); setDraftMYYear('');
                                setDraftDueDateFrom(''); setDraftDueDateTo('');
                                setDraftEstType('');
                                setDraftActId(null); setDraftActSearch('');
                                setDraftRuleId(null); setDraftRuleSearch('');
                                setDraftActivityId(null); setDraftActivitySearch('');
                                setDraftOwner(null); setDraftManager(null);
                                setDraftVertical(null); setDraftDepartment(null);
                                setMonthYear(''); setDueDateFrom(''); setDueDateTo('');
                                setEstTypeFilter('');
                                setActIdFilter(null); setActNameFilter('');
                                setRuleIdFilter(null); setRuleNameFilter('');
                                setActivityIdFilter(null); setActivityNameFilter('');
                                setOwnerFilter(null);
                                setManagerFilter(null); setVerticalFilter(null);
                                setDepartmentFilter(null); setPage(1);
                            }}
                        >
                            Clear All
                        </button>
                        <button
                            className="cl2-mfd-btn cl2-mfd-btn-apply"
                            onClick={() => {
                                setDashboardNav(null);
                                setMonthYear(draftMonthYear);
                                setDueDateFrom(draftDueDateFrom);
                                setDueDateTo(draftDueDateTo);
                                setEstTypeFilter(draftEstType);
                                setActIdFilter(draftActId);
                                setActNameFilter(draftActId ? draftActSearch : '');
                                setRuleIdFilter(draftRuleId);
                                setRuleNameFilter(draftRuleId ? draftRuleSearch : '');
                                setActivityIdFilter(draftActivityId);
                                setActivityNameFilter(draftActivityId ? draftActivitySearch : '');
                                setOwnerFilter(draftOwner ? draftOwner.value : null);
                                setManagerFilter(draftManager ? draftManager.value : null);
                                setVerticalFilter(draftVertical ? draftVertical.value : null);
                                setDepartmentFilter(draftDepartment ? draftDepartment.value : null);
                                setPage(1);
                                setShowMore(false);
                            }}
                        >
                            Apply Filters
                        </button>
                    </div>

                </div>
            </div>
        )}
        </div>
    );
}
